import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, Play, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Phone, Mail, MapPin, MessageCircle, Instagram, Youtube, Facebook, Linkedin, ExternalLink, Mic, Target, Calendar, Check, Home as HomeIcon, HelpCircle, User, Send, MessageSquare, Bell, Plus, Edit2, Trash2, CheckCircle, Circle, Pin, Upload, FileText, Clock, Globe, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

// Enrollment Modal Component
const EnrollmentModal = ({ isOpen, onClose, serviceName, paymentLink }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        instagram: null,
        lead_source: `enrollment-${serviceName.toLowerCase().replace(/\s+/g, '-')}`
      });
      
      // Redirect to payment (placeholder for now)
      if (paymentLink) {
        window.location.href = paymentLink;
      } else {
        // Temporary: show success and close
        alert('Thank you! Payment gateway coming soon. Chelsea will contact you shortly.');
        onClose();
        setFormData({ name: '', email: '', phone: '' });
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="enrollment-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose}></div>
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-burgundy/60 hover:text-burgundy">
          <X className="w-6 h-6" />
        </button>
        
        <div className="text-center mb-6">
          <h3 className="font-h1 text-2xl text-burgundy mb-1">Almost there!</h3>
          <p className="font-body text-burgundy/70">{serviceName}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
              placeholder="Your name"
              data-testid="enroll-name-input"
            />
          </div>
          <div>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
              placeholder="Email address"
              data-testid="enroll-email-input"
            />
          </div>
          <div>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
              placeholder="Phone number"
              data-testid="enroll-phone-input"
            />
          </div>
          
          {error && <p className="text-red-600 font-body text-sm text-center">{error}</p>}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-burgundy text-white py-4 rounded-full font-h1 text-lg hover:bg-burgundy/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="enroll-submit-btn"
          >
            {loading ? 'Please wait...' : 'Continue to Payment →'}
          </button>
        </form>
        
        <p className="font-body text-burgundy/40 text-xs text-center mt-4">
          Secure checkout powered by Cayman Gateway
        </p>
      </div>
    </div>
  );
};

// Testimonials Data - Updated for corporate focus
const testimonials = [
  { quote: "It has been wonderful to be part of the Chelsea Flynn coaching programme. Chelsea is incredibly knowledgeable and passionate, and she really understands and supports you.", name: "Suzanne Reynolds", role: "Coaching Client", image: "https://customer-assets.emergentagent.com/job_207e3b3a-41ee-4cf8-aebc-ff8609c9289a/artifacts/ki7x0e6f_Suzanne%20Image.jpg" },
  { quote: "It has been absolutely refreshing to connect with a diverse group of growth-minded people. This experience has helped me to step out of my comfort zone and do things that add value to my desired purposeful life.", name: "Meishka Zuill", role: "Coaching Client", image: "https://customer-assets.emergentagent.com/job_207e3b3a-41ee-4cf8-aebc-ff8609c9289a/artifacts/oo3f8di6_Meishka%20Image.jpg" },
  { quote: "Expanding my circle has been a great part of my growth. Coaching is also a great way to keep myself accountable.", name: "Rashana Hydes", role: "Coaching Client", image: "https://customer-assets.emergentagent.com/job_207e3b3a-41ee-4cf8-aebc-ff8609c9289a/artifacts/7xj7av27_Rashana.jpg" },
  { quote: "I've noticed a shift in my internal dialogue and better resilience in combating negative thought patterns. Chelsea is teaching me that recalibrating my mindset is essential to success.", name: "Fanisha Nunez-Bush", role: "Coaching Client", image: "https://customer-assets.emergentagent.com/job_207e3b3a-41ee-4cf8-aebc-ff8609c9289a/artifacts/wxwh6581_Fanisha.jpg" },
  { quote: "I want to express my deepest gratitude to my coach, Chelsea. She doesn't just coach, she pours her heart into every session. Chelsea, your impact reaches far beyond words.", name: "Heather Wellington", role: "Coaching Client", image: "https://customer-assets.emergentagent.com/job_faith-transform-1/artifacts/ex3e4rei_Heather.jpg" },
  { quote: "I unequivocally recommend Ms. Chelsea Flynn's coaching services to organizations and professionals aspiring to achieve excellence through disciplined, results-oriented mindset development. Her expertise and dedication make her a standout leader in her field.", name: "Donna Bush", role: "Coaching Client", image: "https://customer-assets.emergentagent.com/job_faith-transform-1/artifacts/ivwu6iwf_Donna%20bi.jpg" },
  { quote: "We try to do it all. Chelsea reminds us that we must take time for ourselves. We must be in a good space to care for our loved ones.", name: "Raquel Jackson", role: "Coaching Client", image: "https://customer-assets.emergentagent.com/job_faith-transform-1/artifacts/csbwdxqr_Racquel.jpg" },
  { quote: "Since embarking on this journey, I have been able to realize and accept how passionate I am with all interactions and connections I make. I have also been able to put my emotional intelligence/awareness into practice which I didn't do a lot of before.", name: "Alikie Kandler", role: "Coaching Client", image: "https://customer-assets.emergentagent.com/job_faith-transform-1/artifacts/6xqk2smy_Alikie.jpg" }
];

// FAQ Data - Updated for High-Performance & Leadership
const faqData = [
  { question: "What is high-performance coaching?", answer: "High-performance coaching helps ambitious professionals and leaders strengthen their performance, resilience, and confidence using evidence-based strategies grounded in neuroscience and CBT. It's focused, strategic, and results-driven." },
  { question: "Who is your coaching for?", answer: "I work with executives, leaders, and ambitious professionals who want to perform at their best without burning out. I also deliver corporate workshops for leadership teams wanting to build resilience and elevate performance across their organisation." },
  { question: "How is coaching different from therapy?", answer: "Coaching is future-focused and action-oriented. We work on performance, goals, and building systems for sustainable success. Therapy typically addresses past trauma and mental health concerns. Coaching complements therapy by helping you move forward with clarity and confidence." },
  { question: "What coaching services do you offer?", answer: "I offer: 1:1 Executive Coaching (US$2,000 for 8 weeks), Breakthrough Session (US$250 for focused clarity), Corporate Workshops (CI$2,000 per 2-hour session), and Speaking Engagements (custom quote)." },
  { question: "How long is the 1:1 coaching programme?", answer: "The 1:1 Executive Coaching programme is 8 weeks with 8 sessions. It's designed to create rapid, measurable leadership breakthroughs in a focused timeframe." },
  { question: "What results can I expect?", answer: "Clients typically experience increased confidence and executive presence, better stress management, clearer decision-making, improved team dynamics, and sustainable high performance. Results depend on your commitment and willingness to do the work." },
  { question: "Do you offer corporate programmes?", answer: "Yes. I offer a 3-month Leadership Performance Partnership including workshops on Preventing Burnout, Psychological Resilience, and Executive Presence. I also offer leadership retainers for ongoing team support." },
  { question: "Do you offer virtual sessions?", answer: "Yes, all coaching sessions are held virtually for your convenience. I work with clients across time zones." },
  { question: "What's your background?", answer: "I bring 15+ years leading high-performing teams, experience founding a successful recruitment agency in the Cayman Islands, and I'm a certified Master Coach specialising in neuroscience-based strategies (NLP, CBT). I'm also Founder & Chair of Autism Acceptance Cayman." },
  { question: "How do I get started?", answer: "Book a free discovery call to discuss your goals and see if we're a good fit. No pressure — just a conversation about what's possible for you." }
];

// Helper to convert file to base64
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
});

// Logo Component
const Logo = ({ className = "" }) => (
  <Link to="/" className={`flex items-center flex-shrink-0 ${className}`}>
    <img src="https://customer-assets.emergentagent.com/job_faith-transform-1/artifacts/0yw0xylc_Chelsea%20Flynn_Additional_red.png" alt="Chelsea Flynn Coaching" className="h-14 w-auto object-contain" />
  </Link>
);

// Floating WhatsApp Button
const FloatingWhatsApp = () => (
  <a href="https://wa.me/13453253924?text=Hi%20Chelsea!%20I%20visited%20your%20website%20and%20would%20like%20to%20connect." target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-all hover:scale-110" data-testid="floating-whatsapp">
    <MessageCircle className="w-6 h-6" />
  </a>
);

// Navigation Component
const Navigation = ({ isScrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navLinks = [
    { name: "Home", href: "/" },
    { name: "Services", href: "/#services" },
    { name: "About", href: "/#about" },
    { name: "Testimonials", href: "/#testimonials" },
    { name: "FAQs", href: "/#faqs" },
    { name: "Contact", href: "/#contact" }
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Logo />
          <div className="hidden md:flex items-center space-x-5">
            {navLinks.map((link) => (<a key={link.name} href={link.href} className={`font-body text-base font-bold transition-colors ${isScrolled ? 'text-burgundy hover:text-burgundy/70' : 'text-white hover:text-white/80'}`} style={{textShadow: isScrolled ? 'none' : '2px 2px 4px rgba(0,0,0,0.6)'}} data-testid={`nav-${link.name.toLowerCase()}`}>{link.name}</a>))}
            <a href="https://www.caymancareer.com/hub" target="_blank" rel="noopener noreferrer" className={`font-body text-base font-bold px-3 py-1 rounded-full whitespace-nowrap transition-colors ${isScrolled ? 'text-burgundy border border-burgundy hover:bg-burgundy hover:text-white' : 'text-white border border-white hover:bg-white hover:text-burgundy'}`} style={{textShadow: isScrolled ? 'none' : '2px 2px 4px rgba(0,0,0,0.6)'}} data-testid="nav-hub">Career Coaching Hub</a>
            <Link to="/get-started" className="btn-solid whitespace-nowrap text-base px-4 py-1.5" data-testid="nav-sign-up">Book Coaching</Link>
          </div>
          <button className={`md:hidden ${isScrolled ? 'text-burgundy' : 'text-white drop-shadow-md'}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-lg">
            {navLinks.map((link) => (<a key={link.name} href={link.href} className="block px-4 py-3 font-body text-burgundy hover:bg-cream-light transition-colors" onClick={() => setMobileMenuOpen(false)}>{link.name}</a>))}
            <a href="https://www.caymancareer.com/hub" target="_blank" rel="noopener noreferrer" className="block px-4 py-3 font-body text-burgundy hover:bg-cream-light transition-colors" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-hub">Career Coaching Hub</a>
            <Link to="/get-started" className="block mx-4 mt-2 btn-solid text-center" onClick={() => setMobileMenuOpen(false)}>Book Coaching</Link>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section - High Performance & Leadership Focus
const HeroSection = () => (
  <section className="relative h-screen" data-testid="hero-section">
    <div className="absolute inset-0 w-full h-full">
      <img src="https://customer-assets.emergentagent.com/job_207e3b3a-41ee-4cf8-aebc-ff8609c9289a/artifacts/zc4ce3t2_20250913-YAN02815%20%282%29.jpg" alt="Chelsea Flynn Coaching Group" className="w-full h-full object-cover object-top" />
      <div className="absolute inset-0 bg-gradient-to-t from-burgundy via-burgundy/60 to-transparent"></div>
    </div>
    <div className="relative z-10 h-full flex flex-col justify-end pb-8 sm:pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-h1 text-base sm:text-lg text-cream-light mb-2 tracking-wider">High-Performance, Leadership & Life Coaching</h2>
          <h1 className="font-h1 text-2xl sm:text-4xl lg:text-5xl text-white mb-4 leading-tight">Transform pressure into peak performance without burnout</h1>
          <p className="font-body text-sm sm:text-lg text-cream-light mb-8 leading-relaxed">Evidence-based coaching for ambitious professionals and leadership teams. Build resilience, elevate confidence, and lead with impact without burn-out.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/get-started" className="btn-solid text-sm sm:text-base" data-testid="hero-get-started">Work With Chelsea</Link>
            <a href="#contact" className="btn-solid text-sm sm:text-base" data-testid="hero-contact">Book a Discovery Call</a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Why Choose Coach Chelsea Section - Now Results/Outcomes Focused
const WhyChooseSection = () => (
  <section id="about" className="py-20 bg-cream-light" data-testid="why-choose-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="font-h2 text-3xl sm:text-5xl text-burgundy mb-4">Results You Can Expect</h2>
        <p className="font-body text-burgundy/80 text-base sm:text-lg max-w-2xl mx-auto">Using the Whole Human Method — evidence-based neuroscience, CBT, and NLP combined with practical leadership experience.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow" data-testid="result-card-1">
          <div className="h-80 overflow-hidden"><img src="https://customer-assets.emergentagent.com/job_db70e33d-7b71-4dc3-aa7e-c570bb7270eb/artifacts/vltqpj10_22%20%281%29.png" alt="Executive Presence" className="w-full h-full object-cover object-center" /></div>
          <div className="p-6 sm:p-8"><h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-4">Executive Presence & Confidence</h3><p className="font-body text-burgundy/80 leading-relaxed text-sm sm:text-base">Elevate your authentic leadership presence. Unlock promotions, increase influence, and build stronger team dynamics through confidence that commands respect.</p></div>
        </div>
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow" data-testid="result-card-2">
          <div className="h-80 overflow-hidden"><img src="https://customer-assets.emergentagent.com/job_db70e33d-7b71-4dc3-aa7e-c570bb7270eb/artifacts/pjv8apbb_IMG_3765.JPG" alt="Stress Management" className="w-full h-full object-cover object-top" /></div>
          <div className="p-6 sm:p-8"><h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-4">Stress & Burnout Prevention</h3><p className="font-body text-burgundy/80 leading-relaxed text-sm sm:text-base">Build psychological resilience that turns pressure into advantage. Eliminate burnout risk and sustain elite performance in relentless environments.</p></div>
        </div>
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow" data-testid="result-card-3">
          <div className="h-80 overflow-hidden"><img src="https://customer-assets.emergentagent.com/job_db70e33d-7b71-4dc3-aa7e-c570bb7270eb/artifacts/xwwukmw3_IMG_2593%20%282%29.JPG" alt="Leadership Performance" className="w-full h-full object-cover object-top" /></div>
          <div className="p-6 sm:p-8"><h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-4">Leadership Performance</h3><p className="font-body text-burgundy/80 leading-relaxed text-sm sm:text-base">Create rapid, measurable leadership breakthroughs. Strategic decision-making, career acceleration, and sustainable high performance for you and your team.</p></div>
        </div>
      </div>
    </div>
  </section>
);

// Meet Your Coach - Expandable Bio Section
const CoachBioSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <section className="py-12 bg-white" data-testid="coach-bio-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-cream-light rounded-2xl p-6 sm:p-8 shadow-md">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden shadow-lg flex-shrink-0">
              <img src="https://customer-assets.emergentagent.com/job_234b3ca2-db26-4aca-93f6-fe604c49b26b/artifacts/2x73rryk_CL-22.jpg" alt="Chelsea Flynn" className="w-full h-full object-cover" style={{objectPosition: '35% 20%'}} />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-h2 text-2xl sm:text-3xl text-burgundy mb-2">Meet Your Coach</h3>
              <p className="font-body text-burgundy/80 text-sm sm:text-base mb-4">
                Chelsea Flynn is an accredited Master Life Transformation Coach and Founder of Chelsea Flynn Coaching. She partners with ambitious professionals and leadership teams to strengthen performance, resilience, and confidence without burnout.
              </p>
              <button 
                onClick={() => setIsExpanded(!isExpanded)} 
                className="inline-flex items-center gap-2 font-body text-burgundy font-semibold hover:text-burgundy/70 transition-colors"
                data-testid="bio-expand-btn"
              >
                {isExpanded ? 'Read Less' : 'Read Full Bio'}
                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {isExpanded && (
                <div className="mt-6 space-y-4 font-body text-burgundy/80 text-sm sm:text-base animate-in fade-in duration-300" data-testid="bio-expanded-content">
                  <p>
                    Holding an LLB (Hons) in Law and bringing 15 years of proven leadership experience, Chelsea has operated in high-stakes environments at the executive level. She co-founded and scaled a leading recruitment agency in the Cayman Islands, giving her first-hand insight into the pressures of executive decision-making, talent retention, and organisational growth.
                  </p>
                  <p>
                    Chelsea is trained and certified in CBT, NLP, Stress & Anxiety Management, Trauma, ILM Level 3 Leadership & Management and Cornell University Performance Leadership. She delivers sharp, practical interventions that create rapid, measurable shifts in mindset, resilience, and leadership performance.
                  </p>
                  <div>
                    <p className="font-semibold text-burgundy mb-2">Her work focuses on:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Executive presence and strategic confidence</li>
                      <li>Stress and burnout prevention</li>
                      <li>Leadership performance and team resilience</li>
                      <li>Career acceleration and high-stakes decision-making</li>
                    </ul>
                  </div>
                  <p>
                    Chelsea works with organisations to deliver corporate workshops and leadership coaching designed to build the resilience, confidence and strategic capability that accelerates career and long-term success. She also provides 1:1 executive and high-performance coaching to accelerate growth and unlock full potential.
                  </p>
                  <p>
                    In addition to her commercial work, Chelsea is Founder and Chair of <a href="https://autismacceptance.ky" target="_blank" rel="noopener noreferrer" className="text-burgundy underline hover:text-burgundy/70">Autism Acceptance</a>, advocating for neurodiversity and inclusive leadership. As a mother of three, including an autistic child and a former competitive amateur bodybuilder, she lives the high-performance principles she teaches, demonstrating that sustainable success is built on resilience, clarity, and inner mastery.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Video Testimonial Card Component
const VideoTestimonialCard = ({ src, thumbnail, index }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  
  const handlePlay = () => {
    setIsPlaying(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };
  
  // Auto-pause when scrolled out of view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && videoRef.current && isPlaying) {
            videoRef.current.pause();
            setIsPlaying(false);
          }
        });
      },
      { threshold: 0.3 }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, [isPlaying]);
  
  return (
    <div ref={containerRef} className="bg-cream-light rounded-2xl overflow-hidden shadow-lg relative flex-shrink-0 w-[70vw] sm:w-[45vw] lg:w-auto" data-testid={`video-testimonial-${index}`}>
      {!isPlaying && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer z-10"
          onClick={handlePlay}
        >
          <img src={thumbnail} alt="Video thumbnail" className="absolute inset-0 w-full h-full object-cover" />
          <div className="relative bg-white/90 rounded-full p-4 shadow-lg hover:scale-110 transition-transform">
            <Play className="w-8 h-8 text-burgundy fill-burgundy" />
          </div>
        </div>
      )}
      <video 
        ref={videoRef}
        className="w-full aspect-[9/16] object-cover bg-cream-light" 
        controls={isPlaying}
        playsInline
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      >
        <source src={src} type="video/quicktime" />
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
};

// Testimonials Section with Videos
const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [videoIndex, setVideoIndex] = useState(0);
  const videoCarouselRef = useRef(null);
  
  useEffect(() => { const interval = setInterval(() => { setCurrentIndex((prev) => (prev + 1) % testimonials.length); }, 5000); return () => clearInterval(interval); }, []);
  
  const videoTestimonials = [
    { src: "https://customer-assets.emergentagent.com/job_234b3ca2-db26-4aca-93f6-fe604c49b26b/artifacts/nqldfufm_IWDVID4.mov", thumbnail: "https://customer-assets.emergentagent.com/job_234b3ca2-db26-4aca-93f6-fe604c49b26b/artifacts/5hay5drx_Pic%204.jpeg" },
    { src: "https://customer-assets.emergentagent.com/job_234b3ca2-db26-4aca-93f6-fe604c49b26b/artifacts/lc1u0pp9_IWD%20VID3.mov", thumbnail: "https://customer-assets.emergentagent.com/job_234b3ca2-db26-4aca-93f6-fe604c49b26b/artifacts/iq66wfe7_Pic%203.jpeg" },
    { src: "https://customer-assets.emergentagent.com/job_234b3ca2-db26-4aca-93f6-fe604c49b26b/artifacts/142p2n92_IWD%20VID2.mov", thumbnail: "https://customer-assets.emergentagent.com/job_234b3ca2-db26-4aca-93f6-fe604c49b26b/artifacts/sytqzkz4_pic%202.jpeg" },
    { src: "https://customer-assets.emergentagent.com/job_234b3ca2-db26-4aca-93f6-fe604c49b26b/artifacts/6f29xddk_IWD%20VID%201.mov", thumbnail: "https://customer-assets.emergentagent.com/job_234b3ca2-db26-4aca-93f6-fe604c49b26b/artifacts/oewduhme_pic%201.jpeg" }
  ];

  // Handle scroll snap for mobile carousel
  const handleVideoScroll = () => {
    if (videoCarouselRef.current) {
      const scrollLeft = videoCarouselRef.current.scrollLeft;
      const cardWidth = videoCarouselRef.current.offsetWidth * 0.7;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setVideoIndex(Math.min(newIndex, videoTestimonials.length - 1));
    }
  };
  
  return (
    <section id="testimonials" className="py-20 bg-burgundy" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12"><h2 className="font-h2 text-3xl sm:text-5xl text-white mb-4">Client Success Stories</h2><p className="font-body text-cream-light">Real results from ambitious professionals</p></div>
        
        {/* Video Testimonials - Swipeable on mobile, Grid on desktop */}
        <div className="mb-16">
          {/* Mobile Carousel */}
          <div 
            ref={videoCarouselRef}
            className="lg:hidden flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onScroll={handleVideoScroll}
          >
            {videoTestimonials.map((video, i) => (
              <div key={i} className="snap-center">
                <VideoTestimonialCard src={video.src} thumbnail={video.thumbnail} index={i} />
              </div>
            ))}
          </div>
          {/* Mobile Dots */}
          <div className="lg:hidden flex justify-center gap-2 mt-4">
            {videoTestimonials.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === videoIndex ? 'bg-white' : 'bg-white/40'}`} />
            ))}
          </div>
          
          {/* Desktop Grid */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-6">
            {videoTestimonials.map((video, i) => (
              <VideoTestimonialCard key={i} src={video.src} thumbnail={video.thumbnail} index={i} />
            ))}
          </div>
        </div>
        
        {/* Rotating Text Testimonials */}
        <div className="relative">
          <div className="overflow-hidden">
            <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
              {testimonials.map((t, i) => (
                <div key={i} className="w-full flex-shrink-0 px-4">
                  <div className="bg-white rounded-2xl p-6 sm:p-12 max-w-4xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8">
                      <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden shadow-lg flex-shrink-0"><img src={t.image} alt={t.name} className={`w-full h-full object-cover ${t.imagePosition === 'center' ? 'object-center' : 'object-top'}`} /></div>
                      <div className="flex-1 text-center md:text-left"><p className="font-body text-base sm:text-lg text-burgundy/80 italic mb-4 sm:mb-6">"{t.quote}"</p><h4 className="font-h2 text-lg sm:text-xl text-burgundy">{t.name}</h4><p className="font-body text-burgundy/60">{t.role}</p></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button onClick={() => setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)} className="absolute left-2 sm:left-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 sm:p-3 shadow-lg" data-testid="testimonial-prev"><ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-burgundy" /></button>
          <button onClick={() => setCurrentIndex((prev) => (prev + 1) % testimonials.length)} className="absolute right-2 sm:right-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 sm:p-3 shadow-lg" data-testid="testimonial-next"><ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-burgundy" /></button>
          <div className="flex justify-center gap-2 mt-8 flex-wrap">{testimonials.map((_, i) => (<button key={i} onClick={() => setCurrentIndex(i)} className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${i === currentIndex ? 'bg-white' : 'bg-white/40'}`} data-testid={`testimonial-dot-${i}`} />))}</div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    { title: "1:1 Coaching", price: "US$2,000", priceNote: "(2 installments available)", description: "8 weeks of focused, strategic transformation.", features: ["8 x 1:1 coaching sessions", "Personalised performance plan", "Neuroscience-based strategies", "CBT & NLP techniques", "Measurable outcomes"], highlighted: true, link: "/services/executive-coaching" },
    { title: "Corporate Workshops", price: "CI$2,000", priceNote: "(per 2-hour session)", description: "Build resilient, high-performing teams.", features: ["Burnout prevention", "Psychological resilience", "Executive presence training", "Leadership team focus", "Customised content"], link: "/services/corporate-workshops" },
    { title: "Breakthrough Session", price: "US$250", description: "Clarity and direction on a specific challenge.", features: ["1-hour focused session", "Immediate action steps", "Flexible scheduling", "Quick breakthroughs"], link: "/services/breakthrough-session" },
    { title: "Speaking Engagement", price: "Custom Quote", description: "Keynotes, workshops & leadership events.", features: ["Corporate events", "Leadership conferences", "Team workshops", "Virtual & in-person"], link: "/services/speaking" }
  ];
  return (
    <section id="services" className="py-20 bg-cream-light" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16"><h2 className="font-h2 text-3xl sm:text-5xl text-burgundy mb-4">How We Work Together</h2><p className="font-body text-burgundy/80 text-lg sm:text-xl">Invest in your performance and lead with impact</p></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <div key={i} className={`rounded-2xl p-5 sm:p-6 ${s.highlighted ? 'bg-burgundy text-white' : 'bg-white text-burgundy'} shadow-lg`} data-testid={`service-card-${i}`}>
              <h3 className="font-h2 text-lg sm:text-xl mb-2">{s.title}</h3>
              <div className="mb-3"><span className="font-h1 text-xl sm:text-2xl">{s.price}</span>{s.priceNote && <p className={`text-xs ${s.highlighted ? 'text-cream-light' : 'text-burgundy/60'}`}>{s.priceNote}</p>}</div>
              <p className={`font-body text-sm mb-3 ${s.highlighted ? 'text-cream-light' : 'text-burgundy/70'}`}>{s.description}</p>
              <ul className="space-y-1 mb-4">{s.features.map((f, j) => (<li key={j} className="flex items-start gap-2 text-xs sm:text-sm font-body"><Check className={`w-3 h-3 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0 ${s.highlighted ? 'text-cream-light' : 'text-burgundy'}`} /><span className={s.highlighted ? 'text-cream-light' : 'text-burgundy/80'}>{f}</span></li>))}</ul>
              <Link to={s.link} className={`block text-center py-2 sm:py-3 px-4 sm:px-6 rounded-full font-h1 text-sm ${s.highlighted ? 'bg-white text-burgundy' : 'bg-burgundy text-white'}`}>Learn More</Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Radio Section
const RadioSection = () => (
  <section className="py-20 bg-white" data-testid="radio-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4"><Mic className="w-6 h-6 sm:w-8 sm:h-8 text-burgundy" /><h2 className="font-h2 text-3xl sm:text-5xl text-burgundy">Radio</h2></div>
        <p className="font-body text-burgundy/80 max-w-3xl mx-auto text-sm sm:text-base">Sharing insights on performance, resilience, and leadership every Saturday 11am-1pm EST on BOBO FM 103.1.</p>
        <div className="flex justify-center mt-6">
          <a href="https://radio.securenetsystems.net/cwa/index.cfm?CFID=mentorship-hub-30&CFTOKEN=0&stationCallSign=BOBO891" target="_blank" rel="noopener noreferrer" className="btn-solid text-sm sm:text-base" data-testid="radio-listen-btn"><ExternalLink className="w-4 h-4 mr-2" />Listen Live</a>
        </div>
      </div>
      <h3 className="font-h2 text-xl sm:text-2xl text-burgundy text-center mb-8">Radio Highlights</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        {[{ src: "CEzbP5n1qtE", title: "Trusting the Process" }, { src: "WeIY2kI7r_o", title: "Outgrowing Your Environment" }, { src: "ouYk4MhYphg", title: "Becoming a Role Model" }].map((v, i) => (
          <div key={i} className="bg-cream-light rounded-2xl overflow-hidden shadow-lg"><div className="aspect-video"><iframe src={`https://www.youtube.com/embed/${v.src}`} title={v.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe></div><div className="p-4 sm:p-6"><h3 className="font-h2 text-lg sm:text-xl text-burgundy">{v.title}</h3></div></div>
        ))}
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <section id="faqs" className="py-20 bg-cream-light" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12"><div className="flex items-center justify-center gap-3 mb-4"><HelpCircle className="w-6 h-6 sm:w-8 sm:h-8 text-burgundy" /><h2 className="font-h2 text-3xl sm:text-5xl text-burgundy">FAQs</h2></div></div>
        <div className="space-y-3 sm:space-y-4">
          {faqData.map((faq, i) => (
            <div key={i} className="bg-white rounded-xl shadow-md overflow-hidden" data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left hover:bg-cream-light/50">
                <span className="font-h2 text-base sm:text-lg text-burgundy pr-4">{faq.question}</span>
                {openIndex === i ? <ChevronUp className="w-5 h-5 text-burgundy flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-burgundy flex-shrink-0" />}
              </button>
              {openIndex === i && <div className="px-4 sm:px-6 pb-4 sm:pb-5"><p className="font-body text-burgundy/80 text-sm sm:text-base">{faq.answer}</p></div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setStatus('Thank you for subscribing! Check your inbox.'); 
      setEmail(''); 
    } catch (err) {
      setStatus('Something went wrong. Please try again.');
    }
    setLoading(false);
  };
  return (
    <section className="py-16 bg-burgundy" data-testid="newsletter-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-h2 text-2xl sm:text-3xl text-white mb-4">Subscribe to Newsletter</h2>
        <p className="font-body text-cream-light mb-6 text-sm sm:text-base">Get leadership insights, performance tips, and updates delivered to your inbox.</p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" className="flex-1 px-4 py-3 rounded-full font-body text-burgundy outline-none" data-testid="newsletter-email" />
          <button type="submit" disabled={loading} className="bg-white text-burgundy px-6 py-3 rounded-full font-h1 text-sm sm:text-base disabled:opacity-50" data-testid="newsletter-submit">{loading ? 'Subscribing...' : 'Subscribe'}</button>
        </form>
        {status && <p className="mt-4 text-cream-light font-body">{status}</p>}
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await axios.post(`${API}/contact`, formData); setStatus({ type: 'success', message: 'Message sent! Chelsea will be in touch soon.' }); setFormData({ name: '', email: '', phone: '', message: '' }); }
    catch { setStatus({ type: 'error', message: 'Failed to send. Please try again.' }); }
    setLoading(false);
  };
  return (
    <section id="contact" className="py-20 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12"><h2 className="font-h2 text-3xl sm:text-5xl text-burgundy mb-4">Get In Touch</h2></div>
        <div className="grid md:grid-cols-2 gap-8 sm:gap-12">
          <div>
            <h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-6">Contact Information</h3>
            <div className="space-y-4">
              <a href="mailto:transform@chelseaflynncoaching.com" className="flex items-center gap-4 font-body text-burgundy text-sm sm:text-base"><div className="w-10 h-10 sm:w-12 sm:h-12 bg-cream-light rounded-full flex items-center justify-center"><Mail className="w-4 h-4 sm:w-5 sm:h-5" /></div>transform@chelseaflynncoaching.com</a>
              <a href="tel:+13453253924" className="flex items-center gap-4 font-body text-burgundy text-sm sm:text-base"><div className="w-10 h-10 sm:w-12 sm:h-12 bg-cream-light rounded-full flex items-center justify-center"><Phone className="w-4 h-4 sm:w-5 sm:h-5" /></div>1 345 325 3924</a>
              <div className="flex items-center gap-4 font-body text-burgundy text-sm sm:text-base"><div className="w-10 h-10 sm:w-12 sm:h-12 bg-cream-light rounded-full flex items-center justify-center"><MapPin className="w-4 h-4 sm:w-5 sm:h-5" /></div>Cayman Islands</div>
            </div>
            <img src="https://customer-assets.emergentagent.com/job_messiah-message/artifacts/sxg4ghbz_CL-53.jpg" alt="Chelsea Flynn" className="mt-8 rounded-2xl shadow-lg w-full max-w-md" />
          </div>
          <div className="bg-cream-light rounded-2xl p-6 sm:p-8">
            <h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-6">Send a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block font-body text-burgundy mb-2 text-sm sm:text-base">Name *</label><input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-dusty-rose outline-none font-body text-sm sm:text-base" data-testid="contact-name" /></div>
              <div><label className="block font-body text-burgundy mb-2 text-sm sm:text-base">Email *</label><input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-dusty-rose outline-none font-body text-sm sm:text-base" data-testid="contact-email" /></div>
              <div><label className="block font-body text-burgundy mb-2 text-sm sm:text-base">Phone</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-dusty-rose outline-none font-body text-sm sm:text-base" data-testid="contact-phone" /></div>
              <div><label className="block font-body text-burgundy mb-2 text-sm sm:text-base">Message *</label><textarea required rows={4} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-dusty-rose outline-none font-body resize-none text-sm sm:text-base" data-testid="contact-message" /></div>
              {status.message && <div className={`p-4 rounded-lg text-sm ${status.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{status.message}</div>}
              <button type="submit" disabled={loading} className="w-full btn-solid disabled:opacity-50" data-testid="contact-submit">{loading ? 'Sending...' : 'Send Message'}</button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-burgundy text-white py-12" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 mb-8">
        <div><img src="https://customer-assets.emergentagent.com/job_faith-transform-1/artifacts/0yw0xylc_Chelsea%20Flynn_Additional_red.png" alt="Chelsea Flynn Coaching" className="h-10 sm:h-12 w-auto mb-4 brightness-0 invert" /><p className="font-body text-cream-light text-xs sm:text-sm">High-performance coaching for ambitious professionals and leadership teams ready to lead with impact.</p></div>
        <div><h4 className="font-h2 text-base sm:text-lg mb-4">Quick Links</h4><ul className="space-y-2 font-body text-cream-light text-xs sm:text-sm"><li><a href="/#about">About</a></li><li><a href="/#services">Services</a></li><li><a href="/#testimonials">Testimonials</a></li><li><a href="/#faqs">FAQs</a></li><li><a href="/#contact">Contact</a></li></ul></div>
        <div><h4 className="font-h2 text-base sm:text-lg mb-4">Connect</h4><div className="flex gap-4"><a href="https://www.linkedin.com/in/chelsea-flynn-20a13a39/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"><Linkedin className="w-5 h-5" /></a><a href="https://youtube.com/@chelseaflynn1111" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"><Youtube className="w-5 h-5" /></a><a href="https://www.instagram.com/chelseaflynncoaching" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"><Instagram className="w-5 h-5" /></a><a href="https://www.facebook.com/profile.php?id=61576592120345" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"><Facebook className="w-5 h-5" /></a></div></div>
      </div>
      <div className="border-t border-white/20 pt-8 text-center"><p className="font-body text-cream-light text-xs sm:text-sm">© {new Date().getFullYear()} Chelsea Flynn Coaching. All rights reserved.</p></div>
    </div>
  </footer>
);

// Home Page
const HomePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);
  return (<div className="min-h-screen" data-testid="home-page"><Navigation isScrolled={isScrolled} /><HeroSection /><WhyChooseSection /><CoachBioSection /><TestimonialsSection /><ServicesSection /><RadioSection /><FAQSection /><NewsletterSection /><ContactSection /><Footer /><FloatingWhatsApp /></div>);
};

// Get Started Page
const GetStartedPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);
  const services = [
    { title: "1:1 Coaching", price: "US$2,000", priceNote: "(2 installments available)", description: "8 weeks of focused, strategic transformation.", features: ["8 x 1:1 coaching sessions", "Personalised performance plan", "Neuroscience-based strategies", "CBT & NLP techniques", "Measurable outcomes"], highlighted: true, link: "/services/executive-coaching" },
    { title: "Corporate Workshops", price: "CI$2,000", priceNote: "(per 2-hour session)", description: "Build resilient, high-performing teams.", features: ["Burnout prevention", "Psychological resilience", "Executive presence training", "Leadership team focus"], link: "/services/corporate-workshops" },
    { title: "Breakthrough Session", price: "US$250", description: "Clarity on a specific challenge.", features: ["1-hour focused session", "Immediate action steps", "Flexible scheduling", "Quick breakthroughs"], link: "/services/breakthrough-session" },
    { title: "Speaking Engagement", price: "Custom Quote", description: "Keynotes, workshops & events.", features: ["Corporate events", "Leadership conferences", "Team workshops", "Virtual & in-person"], link: "/services/speaking" }
  ];
  return (
    <div className="min-h-screen bg-cream-light" data-testid="get-started-page">
      <Navigation isScrolled={isScrolled} />
      <div className="pt-24 sm:pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-8 font-body text-burgundy text-sm sm:text-base" data-testid="back-home-btn"><HomeIcon className="w-5 h-5" />Return Home</button>
          <div className="text-center mb-12"><h1 className="font-h1 text-3xl sm:text-5xl text-burgundy mb-4">Elevate Your Performance</h1><p className="font-body text-burgundy/70 text-lg">Choose the path that fits your goals</p></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <div key={i} className={`rounded-2xl p-5 sm:p-6 ${s.highlighted ? 'bg-burgundy text-white' : 'bg-white text-burgundy'} shadow-lg`} data-testid={`get-started-service-${i}`}>
                <h3 className="font-h2 text-lg sm:text-xl mb-2">{s.title}</h3>
                <div className="mb-3"><span className="font-h1 text-xl sm:text-2xl">{s.price}</span>{s.priceNote && <p className={`text-xs ${s.highlighted ? 'text-cream-light' : 'text-burgundy/60'}`}>{s.priceNote}</p>}</div>
                <p className={`font-body text-sm mb-3 ${s.highlighted ? 'text-cream-light' : 'text-burgundy/70'}`}>{s.description}</p>
                <ul className="space-y-1 mb-4">{s.features.map((f, j) => (<li key={j} className="flex items-start gap-2 text-xs sm:text-sm font-body"><Check className={`w-3 h-3 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0 ${s.highlighted ? 'text-cream-light' : 'text-burgundy'}`} /><span className={s.highlighted ? 'text-cream-light' : 'text-burgundy/80'}>{f}</span></li>))}</ul>
                <Link to={s.link} className={`block text-center py-2 sm:py-3 px-4 sm:px-6 rounded-full font-h1 text-sm ${s.highlighted ? 'bg-white text-burgundy' : 'bg-burgundy text-white'}`}>Learn More</Link>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer /><FloatingWhatsApp />
    </div>
  );
};

// Community Login Page
const CommunityPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);
  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try { const r = await axios.post(`${API}/auth/login`, { email, password }); localStorage.setItem('token', r.data.token); localStorage.setItem('user', JSON.stringify(r.data.user)); navigate('/dashboard'); }
    catch (err) { setError(err.response?.data?.detail || 'Invalid credentials'); }
    setLoading(false);
  };
  return (
    <div className="min-h-screen bg-cream-light" data-testid="community-page">
      <Navigation isScrolled={isScrolled} />
      <div className="pt-24 sm:pt-32 pb-20 px-4 flex items-center justify-center min-h-screen">
        <div className="max-w-md w-full">
          <div className="text-center mb-8"><h1 className="font-h1 text-2xl sm:text-3xl text-burgundy mb-2">Welcome Back</h1><p className="font-body text-burgundy/70 text-sm sm:text-base">Sign in to your CFC Community account</p></div>
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <h2 className="font-h2 text-lg sm:text-xl text-burgundy mb-2">Login</h2>
            <p className="font-body text-burgundy/60 text-xs sm:text-sm mb-6">Enter your credentials</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <div><label className="block font-body text-burgundy mb-2 text-sm">Email</label><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-dusty-rose outline-none font-body text-sm sm:text-base" data-testid="login-email" /></div>
              <div><label className="block font-body text-burgundy mb-2 text-sm">Password</label><input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-dusty-rose outline-none font-body text-sm sm:text-base" data-testid="login-password" /></div>
              {error && <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">{error}</div>}
              <button type="submit" disabled={loading} className="w-full btn-solid disabled:opacity-50" data-testid="login-submit">{loading ? 'Signing In...' : 'Sign In'}</button>
            </form>
            <p className="mt-6 text-center font-body text-burgundy/60 text-xs sm:text-sm">Don't have an account? <Link to="/get-started" className="text-burgundy hover:underline">Sign Up</Link></p>
            <div className="mt-6 pt-6 border-t border-dusty-rose"><p className="font-body text-burgundy/60 text-xs">Demo: transform@chelseaflynncoaching.com / admin123</p></div>
          </div>
        </div>
      </div>
      <FloatingWhatsApp />
    </div>
  );
};

// SMART Goal Quadrant Component with visible checkbox
const GoalQuadrant = ({ title, icon, goals, onAddGoal, onToggleGoal, onDeleteGoal, color }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [newGoal, setNewGoal] = useState({ main: '', subGoals: [''], why: '', timeframe: '' });
  const handleAdd = () => { if (newGoal.main) { onAddGoal({ ...newGoal, category: title.toLowerCase().replace('/', '_').replace(' ', '_') }); setNewGoal({ main: '', subGoals: [''], why: '', timeframe: '' }); setShowAdd(false); } };
  const completed = goals.filter(g => g.achieved).length;
  const total = goals.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className={`bg-white rounded-xl shadow-lg p-4 sm:p-6 border-t-4 ${color}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><span className="text-xl sm:text-2xl">{icon}</span><h3 className="font-h2 text-base sm:text-lg text-burgundy">{title}</h3></div>
        <div className="flex items-center gap-2"><div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-4 border-burgundy flex items-center justify-center"><span className="font-h1 text-xs sm:text-sm text-burgundy">{pct}%</span></div></div>
      </div>
      <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
        {goals.map((g, i) => (
          <div key={i} className={`rounded-lg p-3 ${g.achieved ? 'bg-green-50 border border-green-200' : 'bg-cream-light'}`}>
            <div className="flex items-start gap-2">
              <button onClick={() => onToggleGoal(g.id)} className="mt-1 p-1 rounded hover:bg-burgundy/10" title="Mark as completed">
                {g.achieved ? <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" /> : <Circle className="w-5 h-5 sm:w-6 sm:h-6 text-burgundy/40 hover:text-burgundy" />}
              </button>
              <div className="flex-1">
                <p className={`font-body text-sm font-semibold ${g.achieved ? 'line-through text-burgundy/50' : 'text-burgundy'}`}>{g.main}</p>
                {g.subGoals?.length > 0 && g.subGoals.filter(s=>s).length > 0 && (
                  <ul className="mt-1 ml-2 text-xs text-burgundy/60 space-y-1">
                    {g.subGoals.filter(s=>s).map((s, j) => <li key={j} className="flex items-center gap-1"><span className="w-1 h-1 bg-burgundy/40 rounded-full"></span>{s}</li>)}
                  </ul>
                )}
                {g.why && <p className="text-xs text-burgundy/50 mt-1 italic">Why: {g.why}</p>}
                {g.timeframe && <p className="text-xs text-burgundy/50 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{g.timeframe}</p>}
              </div>
              <button onClick={() => onDeleteGoal(g.id)} className="text-burgundy/40 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
            {g.achieved && <p className="text-xs text-green-600 mt-2 font-semibold">✓ Completed</p>}
          </div>
        ))}
      </div>
      {showAdd ? (
        <div className="bg-cream-light rounded-lg p-3 space-y-2">
          <input placeholder="Main Goal *" value={newGoal.main} onChange={(e) => setNewGoal({ ...newGoal, main: e.target.value })} className="w-full px-3 py-2 rounded border text-sm font-body" />
          {newGoal.subGoals.map((s, i) => (<input key={i} placeholder={`Sub-goal ${i + 1} (optional)`} value={s} onChange={(e) => { const subs = [...newGoal.subGoals]; subs[i] = e.target.value; setNewGoal({ ...newGoal, subGoals: subs }); }} className="w-full px-3 py-2 rounded border text-sm font-body" />))}
          {newGoal.subGoals.length < 5 && <button onClick={() => setNewGoal({ ...newGoal, subGoals: [...newGoal.subGoals, ''] })} className="text-xs text-burgundy hover:underline">+ Add Sub-goal (max 5)</button>}
          <input placeholder="Why is this important?" value={newGoal.why} onChange={(e) => setNewGoal({ ...newGoal, why: e.target.value })} className="w-full px-3 py-2 rounded border text-sm font-body" />
          <input placeholder="Timeframe (e.g., By March 2026)" value={newGoal.timeframe} onChange={(e) => setNewGoal({ ...newGoal, timeframe: e.target.value })} className="w-full px-3 py-2 rounded border text-sm font-body" />
          <div className="flex gap-2"><button onClick={handleAdd} className="flex-1 bg-burgundy text-white py-2 rounded text-sm font-h1">Save Goal</button><button onClick={() => setShowAdd(false)} className="flex-1 bg-gray-200 text-burgundy py-2 rounded text-sm font-h1">Cancel</button></div>
        </div>
      ) : (<button onClick={() => setShowAdd(true)} className="w-full py-2 border-2 border-dashed border-burgundy/30 rounded-lg text-burgundy/60 font-body text-sm hover:bg-cream-light hover:border-burgundy/50"><Plus className="w-4 h-4 inline mr-1" />Add Goal</button>)}
    </div>
  );
};

// Dashboard Page with Profile section and improved session notes
const DashboardPage = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('goals');
  const [goals, setGoals] = useState({ spiritual: [], personal: [], career_financial: [], fun_adventure: [] });
  const [sessionNotes, setSessionNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteAttachment, setNoteAttachment] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState('');
  const [bio, setBio] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('user'); const token = localStorage.getItem('token');
    if (!stored || !token) { navigate('/community'); return; }
    setUser(JSON.parse(stored));
    const savedGoals = localStorage.getItem('smartGoals'); if (savedGoals) setGoals(JSON.parse(savedGoals));
    const savedNotes = localStorage.getItem('sessionNotes'); if (savedNotes) setSessionNotes(JSON.parse(savedNotes));
    setProfilePhoto(localStorage.getItem('dashboardPhoto') || '');
    setBio(localStorage.getItem('dashboardBio') || '');
  }, [navigate]);

  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/community'); };
  
  const addGoal = (category, goal) => {
    const newGoals = { ...goals, [category]: [...goals[category], { ...goal, id: Date.now(), achieved: false }] };
    setGoals(newGoals); localStorage.setItem('smartGoals', JSON.stringify(newGoals));
  };
  
  const toggleGoal = (category, id) => {
    const newGoals = { ...goals, [category]: goals[category].map(g => g.id === id ? { ...g, achieved: !g.achieved } : g) };
    setGoals(newGoals); localStorage.setItem('smartGoals', JSON.stringify(newGoals));
  };
  
  const deleteGoal = (category, id) => {
    const newGoals = { ...goals, [category]: goals[category].filter(g => g.id !== id) };
    setGoals(newGoals); localStorage.setItem('smartGoals', JSON.stringify(newGoals));
  };
  
  const handleNoteFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const base64 = await fileToBase64(file);
      setNoteAttachment({ name: file.name, data: base64, type: file.type });
    }
  };
  
  const addSessionNote = () => {
    if (newNote || noteAttachment) {
      const now = new Date();
      const timestamp = now.toLocaleString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      const note = { id: Date.now(), text: newNote, attachment: noteAttachment, timestamp };
      const notes = [note, ...sessionNotes];
      setSessionNotes(notes); localStorage.setItem('sessionNotes', JSON.stringify(notes));
      setNewNote(''); setNoteAttachment(null);
    }
  };

  const saveProfile = () => {
    localStorage.setItem('dashboardPhoto', profilePhoto);
    localStorage.setItem('dashboardBio', bio);
    alert('Profile saved!');
  };

  const totalGoals = Object.values(goals).flat().length;
  const completedGoals = Object.values(goals).flat().filter(g => g.achieved).length;
  const overallProgress = totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0;

  if (!user) return <div className="min-h-screen bg-cream-light flex items-center justify-center"><div className="text-burgundy font-body">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-cream-light" data-testid="dashboard-page">
      <nav className="bg-white shadow-md py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <Logo />
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="font-body text-burgundy text-xs sm:text-base hidden sm:block">Welcome, {user?.name}</span>
            <Link to="/forum" className="text-burgundy hover:text-burgundy/70"><MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" /></Link>
            <Link to="/messages" className="text-burgundy hover:text-burgundy/70 relative"><Bell className="w-5 h-5 sm:w-6 sm:h-6" /></Link>
            <Link to="/profile" className="text-burgundy hover:text-burgundy/70"><User className="w-5 h-5 sm:w-6 sm:h-6" /></Link>
            <button onClick={handleLogout} className="btn-solid text-xs sm:text-sm py-2 px-3 sm:px-4" data-testid="logout-btn">Logout</button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Profile Card with Photo and Bio */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="relative">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-cream-light border-4 border-burgundy">
                {profilePhoto ? <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" /> : <User className="w-10 h-10 sm:w-12 sm:h-12 text-burgundy/30 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />}
              </div>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h2 className="font-h2 text-xl sm:text-2xl text-burgundy">{user?.name}</h2>
              {bio ? <p className="font-body text-burgundy/70 text-sm mt-1">{bio}</p> : <p className="font-body text-burgundy/50 text-sm italic mt-1">Add a bio below...</p>}
            </div>
            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-burgundy flex items-center justify-center bg-cream-light"><span className="font-h1 text-lg sm:text-2xl text-burgundy">{overallProgress}%</span></div>
              <p className="font-body text-xs text-burgundy/60 mt-1">Overall Progress</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-cream-light grid sm:grid-cols-2 gap-3">
            <input type="text" value={profilePhoto} onChange={(e) => setProfilePhoto(e.target.value)} placeholder="Profile photo URL" className="px-3 py-2 rounded-lg border border-dusty-rose outline-none font-body text-sm" />
            <input type="text" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Brief bio about yourself" className="px-3 py-2 rounded-lg border border-dusty-rose outline-none font-body text-sm" />
            <button onClick={saveProfile} className="sm:col-span-2 btn-solid text-sm">Save Profile</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 sm:gap-4 mb-6 overflow-x-auto pb-2">
          {[{ id: 'goals', label: 'SMART Goals', icon: Target }, { id: 'notes', label: 'Session Notes', icon: Edit2 }].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full font-h1 text-xs sm:text-sm whitespace-nowrap ${activeTab === t.id ? 'bg-burgundy text-white' : 'bg-white text-burgundy'}`}><t.icon className="w-4 h-4" />{t.label}</button>
          ))}
          <Link to="/forum" className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full font-h1 text-xs sm:text-sm whitespace-nowrap bg-white text-burgundy"><MessageSquare className="w-4 h-4" />Community Forum</Link>
        </div>

        {activeTab === 'goals' && (
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            <GoalQuadrant title="Spiritual Goals" icon="✝️" goals={goals.spiritual} onAddGoal={(g) => addGoal('spiritual', g)} onToggleGoal={(id) => toggleGoal('spiritual', id)} onDeleteGoal={(id) => deleteGoal('spiritual', id)} color="border-purple-500" />
            <GoalQuadrant title="Personal Goals" icon="🌱" goals={goals.personal} onAddGoal={(g) => addGoal('personal', g)} onToggleGoal={(id) => toggleGoal('personal', id)} onDeleteGoal={(id) => deleteGoal('personal', id)} color="border-green-500" />
            <GoalQuadrant title="Career/Financial Goals" icon="💼" goals={goals.career_financial} onAddGoal={(g) => addGoal('career_financial', g)} onToggleGoal={(id) => toggleGoal('career_financial', id)} onDeleteGoal={(id) => deleteGoal('career_financial', id)} color="border-blue-500" />
            <GoalQuadrant title="Fun/Adventure Goals" icon="🎯" goals={goals.fun_adventure} onAddGoal={(g) => addGoal('fun_adventure', g)} onToggleGoal={(id) => toggleGoal('fun_adventure', id)} onDeleteGoal={(id) => deleteGoal('fun_adventure', id)} color="border-orange-500" />
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
            <h3 className="font-h2 text-lg sm:text-xl text-burgundy mb-4">Session Notes</h3>
            <div className="space-y-3 mb-4">
              <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add notes from your coaching session..." className="w-full px-4 py-3 rounded-lg border border-dusty-rose outline-none font-body text-sm resize-none" rows={3} />
              <div className="flex flex-col sm:flex-row gap-2">
                <input type="file" ref={fileInputRef} onChange={handleNoteFileChange} className="hidden" accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png" />
                <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 border border-dusty-rose rounded-lg text-burgundy font-body text-sm hover:bg-cream-light">
                  <Upload className="w-4 h-4" />
                  {noteAttachment ? noteAttachment.name : 'Attach File'}
                </button>
                <button onClick={addSessionNote} className="flex-1 btn-solid"><Plus className="w-4 h-4 mr-1" />Add Note</button>
              </div>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {sessionNotes.length === 0 ? (
                <p className="text-center text-burgundy/50 font-body py-8">No session notes yet. Add your first note above!</p>
              ) : sessionNotes.map(n => (
                <div key={n.id} className="bg-cream-light rounded-lg p-4">
                  <div className="flex items-center gap-2 text-xs text-burgundy/50 mb-2">
                    <Clock className="w-3 h-3" />
                    <span className="font-body">{n.timestamp}</span>
                  </div>
                  {n.text && <p className="font-body text-burgundy text-sm">{n.text}</p>}
                  {n.attachment && (
                    <div className="mt-2 flex items-center gap-2 p-2 bg-white rounded border border-dusty-rose">
                      <FileText className="w-4 h-4 text-burgundy" />
                      <span className="font-body text-sm text-burgundy">{n.attachment.name}</span>
                      {n.attachment.type.startsWith('image/') && <img src={n.attachment.data} alt="Attachment" className="ml-auto max-h-20 rounded" />}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <FloatingWhatsApp />
    </div>
  );
};

// Profile Page
const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [aboutMe, setAboutMe] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/community'); return; }
    setUser(JSON.parse(stored));
    setAboutMe(localStorage.getItem('aboutMe') || '');
    setProfilePhoto(localStorage.getItem('profilePhoto') || '');
  }, [navigate]);

  const handleSave = () => { localStorage.setItem('aboutMe', aboutMe); localStorage.setItem('profilePhoto', profilePhoto); alert('Profile saved!'); };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-cream-light" data-testid="profile-page">
      <nav className="bg-white shadow-md py-3 sm:py-4"><div className="max-w-7xl mx-auto px-4 flex justify-between items-center"><Logo /><Link to="/dashboard" className="btn-solid text-sm">Back to Dashboard</Link></div></nav>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          <h2 className="font-h2 text-2xl text-burgundy mb-6">My Profile</h2>
          <div className="flex flex-col items-center mb-6">
            <div className="relative w-32 h-32 rounded-full overflow-hidden bg-cream-light mb-4">{profilePhoto ? <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" /> : <User className="w-16 h-16 text-burgundy/30 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />}</div>
            <input type="text" value={profilePhoto} onChange={(e) => setProfilePhoto(e.target.value)} placeholder="Paste image URL for profile photo" className="w-full px-4 py-2 rounded-lg border border-dusty-rose outline-none font-body text-sm" />
          </div>
          <div className="space-y-4">
            <div><label className="block font-body text-burgundy mb-2">Name</label><input type="text" value={user.name} disabled className="w-full px-4 py-3 rounded-lg border border-dusty-rose bg-gray-50 font-body" /></div>
            <div><label className="block font-body text-burgundy mb-2">Email</label><input type="email" value={user.email} disabled className="w-full px-4 py-3 rounded-lg border border-dusty-rose bg-gray-50 font-body" /></div>
            <div><label className="block font-body text-burgundy mb-2">About Me</label><textarea value={aboutMe} onChange={(e) => setAboutMe(e.target.value)} rows={4} placeholder="Tell us about yourself..." className="w-full px-4 py-3 rounded-lg border border-dusty-rose outline-none font-body resize-none" /></div>
            <button onClick={handleSave} className="w-full btn-solid">Save Profile</button>
          </div>
        </div>
      </div>
      <FloatingWhatsApp />
    </div>
  );
};

// Forum Page with Important Notices and File Upload
const ForumPage = () => {
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [notices, setNotices] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [newPhoto, setNewPhoto] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [newNotice, setNewNotice] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/community'); return; }
    const userData = JSON.parse(stored);
    setUser(userData);
    setIsAdmin(userData.email === 'transform@chelseaflynncoaching.com');
    
    const savedPosts = localStorage.getItem('forumPosts');
    if (savedPosts) setPosts(JSON.parse(savedPosts));
    else {
      const seed = [{ id: 1, author: 'Chelsea Flynn', authorPhoto: 'https://customer-assets.emergentagent.com/job_messiah-message/artifacts/sxg4ghbz_CL-53.jpg', type: 'reflection', title: 'Daily Reflection', content: "Today's reflection: What are you grateful for? Take a moment to write down 3 things that brought you joy today. Remember, gratitude shifts our perspective and opens our hearts to receive more blessings.", date: new Date().toLocaleDateString(), comments: [], likes: 5 }];
      setPosts(seed); localStorage.setItem('forumPosts', JSON.stringify(seed));
    }
    
    const savedNotices = localStorage.getItem('forumNotices');
    if (savedNotices) setNotices(JSON.parse(savedNotices));
  }, [navigate]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const base64 = await fileToBase64(file);
      setNewPhoto(base64);
    }
  };

  const addPost = () => {
    if (newPost || newPhoto) {
      const post = { id: Date.now(), author: user.name, authorPhoto: localStorage.getItem('profilePhoto') || '', type: 'post', content: newPost, photo: newPhoto, date: new Date().toLocaleDateString(), comments: [], likes: 0 };
      const updated = [post, ...posts]; setPosts(updated); localStorage.setItem('forumPosts', JSON.stringify(updated)); setNewPost(''); setNewPhoto(null);
    }
  };

  const addNotice = () => {
    if (newNotice && isAdmin) {
      const notice = { id: Date.now(), text: newNotice, date: new Date().toLocaleDateString() };
      const updated = [notice, ...notices]; setNotices(updated); localStorage.setItem('forumNotices', JSON.stringify(updated)); setNewNotice('');
    }
  };

  const deleteNotice = (id) => {
    const updated = notices.filter(n => n.id !== id); setNotices(updated); localStorage.setItem('forumNotices', JSON.stringify(updated));
  };

  const addComment = (postId, comment) => {
    const updated = posts.map(p => p.id === postId ? { ...p, comments: [...p.comments, { id: Date.now(), author: user.name, text: comment, date: new Date().toLocaleDateString() }] } : p);
    setPosts(updated); localStorage.setItem('forumPosts', JSON.stringify(updated));
  };

  const likePost = (postId) => {
    const updated = posts.map(p => p.id === postId ? { ...p, likes: p.likes + 1 } : p);
    setPosts(updated); localStorage.setItem('forumPosts', JSON.stringify(updated));
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-cream-light" data-testid="forum-page">
      <nav className="bg-white shadow-md py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <Logo />
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/dashboard" className="text-burgundy font-body text-sm">Dashboard</Link>
            <Link to="/messages" className="text-burgundy"><Bell className="w-5 h-5" /></Link>
            <Link to="/profile" className="text-burgundy"><User className="w-5 h-5" /></Link>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
        <h1 className="font-h2 text-2xl sm:text-3xl text-burgundy mb-6">CFC Community</h1>

        {/* Important Notices (Admin can add/delete) */}
        {(notices.length > 0 || isAdmin) && (
          <div className="bg-burgundy rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
            <div className="flex items-center gap-2 mb-4"><Pin className="w-5 h-5 text-white" /><h2 className="font-h2 text-lg text-white">Important Notices</h2></div>
            {isAdmin && (
              <div className="flex gap-2 mb-4">
                <input type="text" value={newNotice} onChange={(e) => setNewNotice(e.target.value)} placeholder="Add a pinned notice..." className="flex-1 px-3 py-2 rounded-lg font-body text-sm" />
                <button onClick={addNotice} className="bg-white text-burgundy px-4 py-2 rounded-lg font-h1 text-sm">Pin</button>
              </div>
            )}
            <div className="space-y-2">
              {notices.map(n => (
                <div key={n.id} className="bg-white/10 rounded-lg p-3 flex items-start justify-between">
                  <div><p className="font-body text-white text-sm">{n.text}</p><p className="font-body text-white/60 text-xs mt-1">{n.date}</p></div>
                  {isAdmin && <button onClick={() => deleteNotice(n.id)} className="text-white/60 hover:text-white"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Post with File Upload */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
          <textarea value={newPost} onChange={(e) => setNewPost(e.target.value)} placeholder="Share your thoughts, reflections, or encouragement..." className="w-full px-4 py-3 rounded-lg border border-dusty-rose outline-none font-body resize-none text-sm" rows={3} />
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} className="hidden" accept="image/*" />
            <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 border border-dusty-rose rounded-lg text-burgundy font-body text-sm hover:bg-cream-light">
              <Upload className="w-4 h-4" />
              {newPhoto ? 'Photo Selected' : 'Add Photo'}
            </button>
            {newPhoto && <img src={newPhoto} alt="Preview" className="h-12 rounded" />}
            <button onClick={addPost} className="flex-1 btn-solid text-sm"><Send className="w-4 h-4 mr-2" />Post</button>
          </div>
        </div>

        {/* Posts */}
        <div className="space-y-4">
          {posts.map(p => (
            <div key={p.id} className={`bg-white rounded-2xl shadow-lg p-4 sm:p-6 ${p.type === 'reflection' ? 'border-l-4 border-burgundy' : ''}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-cream-light">{p.authorPhoto ? <img src={p.authorPhoto} alt={p.author} className="w-full h-full object-cover" /> : <User className="w-6 h-6 text-burgundy/30 m-2" />}</div>
                <div><p className="font-h2 text-burgundy text-sm">{p.author}</p><p className="font-body text-burgundy/50 text-xs">{p.date}</p></div>
                {p.type === 'reflection' && <span className="ml-auto bg-burgundy text-white px-2 py-1 rounded text-xs font-h1">Daily Reflection</span>}
              </div>
              {p.title && <h3 className="font-h2 text-lg text-burgundy mb-2">{p.title}</h3>}
              <p className="font-body text-burgundy/80 text-sm mb-3">{p.content}</p>
              {p.photo && <img src={p.photo} alt="Post" className="rounded-lg mb-3 max-h-64 object-cover w-full" />}
              <div className="flex items-center gap-4 border-t border-cream-light pt-3">
                <button onClick={() => likePost(p.id)} className="flex items-center gap-1 text-burgundy/60 hover:text-burgundy text-sm"><span>❤️</span>{p.likes}</button>
                <span className="flex items-center gap-1 text-burgundy/60 text-sm"><MessageSquare className="w-4 h-4" />{p.comments.length}</span>
              </div>
              {p.comments.length > 0 && (<div className="mt-3 space-y-2 pl-4 border-l-2 border-cream-light">{p.comments.map(c => (<div key={c.id} className="text-sm"><span className="font-h2 text-burgundy">{c.author}:</span> <span className="font-body text-burgundy/70">{c.text}</span></div>))}</div>)}
              <CommentInput postId={p.id} onAdd={addComment} />
            </div>
          ))}
        </div>
      </div>
      <FloatingWhatsApp />
    </div>
  );
};

const CommentInput = ({ postId, onAdd }) => {
  const [text, setText] = useState('');
  const handleSubmit = () => { if (text) { onAdd(postId, text); setText(''); } };
  return (<div className="flex gap-2 mt-3"><input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment..." className="flex-1 px-3 py-2 rounded-full border border-dusty-rose outline-none font-body text-sm" onKeyPress={(e) => e.key === 'Enter' && handleSubmit()} /><button onClick={handleSubmit} className="bg-burgundy text-white px-3 py-2 rounded-full"><Send className="w-4 h-4" /></button></div>);
};

// Messages Page
const MessagesPage = () => {
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/community'); return; }
    setUser(JSON.parse(stored));
    const savedMessages = localStorage.getItem('privateMessages');
    if (savedMessages) setMessages(JSON.parse(savedMessages));
  }, [navigate]);

  const sendMessage = () => {
    if (newMessage && selectedChat) {
      const msg = { id: Date.now(), from: user.name, to: selectedChat, text: newMessage, date: new Date().toLocaleString() };
      const updated = [...messages, msg]; setMessages(updated); localStorage.setItem('privateMessages', JSON.stringify(updated)); setNewMessage('');
    }
  };

  const chats = [...new Set(messages.map(m => m.from === user?.name ? m.to : m.from))];

  if (!user) return null;

  return (
    <div className="min-h-screen bg-cream-light" data-testid="messages-page">
      <nav className="bg-white shadow-md py-3 sm:py-4"><div className="max-w-7xl mx-auto px-4 flex justify-between items-center"><Logo /><Link to="/dashboard" className="btn-solid text-sm">Dashboard</Link></div></nav>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <h1 className="font-h2 text-2xl text-burgundy mb-6">Messages</h1>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <h3 className="font-h2 text-burgundy mb-4">Conversations</h3>
            {chats.length === 0 ? <p className="font-body text-burgundy/60 text-sm">No messages yet</p> : chats.map(c => (<button key={c} onClick={() => setSelectedChat(c)} className={`w-full text-left px-3 py-2 rounded-lg font-body text-sm ${selectedChat === c ? 'bg-burgundy text-white' : 'hover:bg-cream-light text-burgundy'}`}>{c}</button>))}
            <div className="mt-4 pt-4 border-t border-cream-light"><input type="text" placeholder="Start new chat with..." onKeyPress={(e) => e.key === 'Enter' && setSelectedChat(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-dusty-rose outline-none font-body text-sm" /></div>
          </div>
          <div className="md:col-span-2 bg-white rounded-xl shadow-lg p-4 flex flex-col h-96">
            {selectedChat ? (<><h3 className="font-h2 text-burgundy mb-4">Chat with {selectedChat}</h3><div className="flex-1 overflow-y-auto space-y-2">{messages.filter(m => (m.from === user.name && m.to === selectedChat) || (m.from === selectedChat && m.to === user.name)).map(m => (<div key={m.id} className={`p-2 rounded-lg max-w-[80%] ${m.from === user.name ? 'bg-burgundy text-white ml-auto' : 'bg-cream-light text-burgundy'}`}><p className="font-body text-sm">{m.text}</p><p className="text-xs opacity-60">{m.date}</p></div>))}</div><div className="flex gap-2 mt-4"><input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Type a message..." className="flex-1 px-4 py-2 rounded-full border border-dusty-rose outline-none font-body text-sm" onKeyPress={(e) => e.key === 'Enter' && sendMessage()} /><button onClick={sendMessage} className="bg-burgundy text-white px-4 py-2 rounded-full"><Send className="w-4 h-4" /></button></div></>) : (<div className="flex-1 flex items-center justify-center"><p className="font-body text-burgundy/60">Select a conversation</p></div>)}
          </div>
        </div>
      </div>
      <FloatingWhatsApp />
    </div>
  );
};

// 1:1 Life Transformation Programme Page
// Executive Coaching Page (formerly Life Transformation)
const LifeTransformationPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);
  
  const weeklyBreakdown = [
    { week: "Week 1", title: "Launch In", desc: "Deep-dive intake session to understand your current challenges, leadership context, and desired outcomes. We establish clear, measurable goals for the programme." },
    { week: "Week 2", title: "Your Leadership Story", desc: "Map your career journey and identify patterns. What's worked, what hasn't, and where you want to go. Uncover blind spots holding you back from peak performance." },
    { week: "Week 3", title: "SMART Goals for Success", desc: "Align personal, career/financial, fun, and spiritual goals. The whole human approach to goal-setting that makes goals feel achievable and creates momentum from day one." },
    { week: "Week 4", title: "The Change Cycle", desc: "Understand how real change happens in the brain (neuroplasticity). Learn to leverage discomfort as a catalyst for growth and breakthrough." },
    { week: "Week 5", title: "Rewiring Limiting Beliefs", desc: "CBT deep-dive: identify and challenge limiting beliefs ('I'm not ready', 'I'm an imposter', 'I can't handle more'). Replace them with evidence-based confidence." },
    { week: "Week 6", title: "Emotional Intelligence & Resilience", desc: "Build self-awareness and develop psychological resilience. Master emotional regulation to perform under pressure without burnout." },
    { week: "Week 7", title: "Executive Presence & High-Performance Habits", desc: "Develop authentic executive presence. Build atomic habits that compound into sustainable high performance." },
    { week: "Week 8", title: "Integration & Launch Forward", desc: "Celebrate breakthroughs. Create a sustainable plan to protect your progress, maintain boundaries, and continue leading with impact." }
  ];

  const expectations = [
    "Elevate your executive presence and authentic leadership confidence",
    "Master stress and prevent burnout using neuroscience-backed strategies",
    "Identify and overcome limiting beliefs keeping you stuck or playing small",
    "Develop psychological resilience that turns pressure into advantage",
    "Build emotional intelligence for better decision-making and team dynamics",
    "Create sustainable high-performance habits that compound over time",
    "Gain clarity on your career trajectory and strategic next moves",
    "Walk away confident, focused, and equipped to lead with impact"
  ];

  return (
    <div className="min-h-screen bg-cream-light" data-testid="executive-coaching-page">
      <Navigation isScrolled={isScrolled} />
      <div className="pt-20 sm:pt-24">
        {/* Hero Section */}
        <div className="relative">
          <div className="absolute inset-0 bg-burgundy/85"></div>
          <img src="https://customer-assets.emergentagent.com/job_db70e33d-7b71-4dc3-aa7e-c570bb7270eb/artifacts/zm7rjch7_IMG_8238%20%282%29.JPG" alt="1:1 Executive Coaching Session" className="w-full h-[550px] sm:h-[650px] object-cover opacity-40" style={{objectPosition: 'center 30%'}} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-4xl mx-auto px-4 text-center text-white">
              <h1 className="font-h1 text-4xl sm:text-5xl lg:text-6xl mb-4">1:1 Coaching</h1>
              <p className="font-h2 text-xl sm:text-2xl lg:text-3xl mb-2">Reset and Recalibrate: the Whole-Human Coaching Method</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mt-6 mb-8">
                <span className="font-h1 text-3xl sm:text-4xl lg:text-5xl">US$2,000</span>
                <span className="font-body text-cream-light text-base sm:text-lg">Or 2 monthly installments of US$1,000</span>
              </div>
              <button onClick={() => setShowEnrollModal(true)} className="inline-block bg-white text-burgundy px-8 py-4 rounded-full font-h1 text-lg hover:bg-cream-light transition-colors cursor-pointer" data-testid="executive-coaching-enroll-top">Apply Now</button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
          {/* Intro */}
          <div className="mb-12">
            <h2 className="font-h2 text-2xl sm:text-3xl lg:text-4xl text-burgundy mb-6 text-center">TRANSFORM PRESSURE INTO PEAK PERFORMANCE</h2>
            <div className="font-body text-burgundy/80 space-y-4 text-base sm:text-lg leading-relaxed">
              <p>High performance shouldn't mean burnout. Yet too many ambitious professionals and leaders sacrifice their wellbeing trying to keep up in relentless environments.</p>
              <p>With 15+ years leading high-performing teams, experience founding a successful recruitment agency, and a background as an executive, mother of three, and competitive bodybuilder — I understand the real pressures leaders face.</p>
              <p className="font-h2 text-burgundy text-lg sm:text-xl">I know what it takes to perform at the highest level while maintaining your edge and your wellbeing.</p>
              <p>That's why this programme exists.</p>
              <p><strong>This is not generic coaching.</strong></p>
              <p>This is evidence-based, whole-human transformation using neuroscience, CBT, and NLP — combined with practical leadership experience to deliver rapid, measurable breakthroughs.</p>
            </div>
          </div>

          {/* What You Can Expect */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-12">
            <h3 className="font-h2 text-xl sm:text-2xl lg:text-3xl text-burgundy mb-6">What You Can Expect</h3>
            <p className="font-body text-burgundy/70 mb-6 text-base sm:text-lg">Over the course of 8 weeks, you'll:</p>
            <ul className="space-y-4">
              {expectations.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-burgundy mt-0.5 flex-shrink-0" />
                  <span className="font-body text-burgundy/80 text-base sm:text-lg">{item}</span>
                </li>
              ))}
            </ul>
            <p className="font-body text-burgundy/70 mt-6 text-base sm:text-lg italic">This isn't about quick fixes — it's about building a sustainable system for high performance.</p>
          </div>

          {/* Program Breakdown */}
          <div className="mb-12">
            <h3 className="font-h2 text-xl sm:text-2xl lg:text-3xl text-burgundy mb-6 text-center">Programme Breakdown</h3>
            <div className="space-y-4">
              {weeklyBreakdown.map((week, i) => (
                <div key={i} className={`rounded-2xl p-5 sm:p-6 ${i % 2 === 0 ? 'bg-burgundy text-white' : 'bg-white text-burgundy shadow-lg'}`}>
                  <div className="flex items-start gap-4">
                    <div className={`font-h1 text-lg sm:text-xl ${i % 2 === 0 ? 'text-cream-light' : 'text-burgundy/60'}`}>{week.week}</div>
                    <div>
                      <h4 className={`font-h2 text-base sm:text-lg lg:text-xl mb-2 ${i % 2 === 0 ? 'text-white' : 'text-burgundy'}`}>{week.title}</h4>
                      <p className={`font-body text-base sm:text-lg ${i % 2 === 0 ? 'text-cream-light' : 'text-burgundy/70'}`}>{week.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Investment Section */}
          <div className="bg-burgundy rounded-2xl p-6 sm:p-8 text-center text-white mb-12">
            <h3 className="font-h2 text-xl sm:text-2xl lg:text-3xl mb-4">Your Investment</h3>
            <div className="font-h1 text-3xl sm:text-4xl lg:text-5xl mb-2">US$2,000</div>
            <p className="font-body text-cream-light text-base sm:text-lg mb-6">(or 2 monthly payments of US$1,000)</p>
            <p className="font-body text-cream-light text-base sm:text-lg mb-6">This 8-week intensive is intentionally limited to ensure deep, focused work with each client.</p>
            <p className="font-body text-cream-light text-base sm:text-lg mb-8">With your commitment and willingness to do the work, transformation is inevitable.</p>
            <p className="font-h2 text-lg sm:text-xl lg:text-2xl mb-8">This is your moment to stop surviving and start leading with impact.</p>
            <button onClick={() => setShowEnrollModal(true)} className="inline-block bg-white text-burgundy px-8 py-4 rounded-full font-h1 text-lg hover:bg-cream-light transition-colors cursor-pointer" data-testid="executive-coaching-enroll">Apply Now</button>
          </div>

          {/* Back Link */}
          <div className="text-center">
            <Link to="/get-started" className="font-body text-burgundy hover:underline flex items-center justify-center gap-2 text-base sm:text-lg">
              <ChevronLeft className="w-5 h-5" />View All Services
            </Link>
          </div>
        </div>
      </div>
      <EnrollmentModal 
        isOpen={showEnrollModal} 
        onClose={() => setShowEnrollModal(false)} 
        serviceName="1:1 Executive Coaching" 
        paymentLink="https://cfc-signup.caymangateway.online/"
      />
      <Footer /><FloatingWhatsApp />
    </div>
  );
};

// Corporate Workshops Page (formerly CFC Community)
const CorporateWorkshopsPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);

  const workshops = [
    { 
      title: "Preventing Burnout in High-Pressure Roles", 
      desc: "Equip your team with practical strategies to recognise early warning signs of burnout and build sustainable performance habits.",
      outcomes: ["Identify personal stress triggers and warning signs", "Master evidence-based stress management techniques", "Build sustainable high-performance routines", "Create team accountability systems"]
    },
    { 
      title: "Psychological Resilience for Leadership Teams", 
      desc: "Build the mental toughness your leaders need to thrive under pressure and navigate uncertainty with confidence.",
      outcomes: ["Develop a growth mindset under pressure", "Master cognitive reframing techniques", "Build emotional regulation skills", "Create team resilience protocols"]
    },
    { 
      title: "Confidence & Executive Presence for Emerging Leaders", 
      desc: "Develop the authentic presence and confidence your rising talent needs to step into leadership roles.",
      outcomes: ["Project authentic confidence in high-stakes situations", "Master body language and communication", "Overcome imposter syndrome", "Build personal brand and influence"]
    }
  ];

  return (
    <div className="min-h-screen bg-cream-light" data-testid="corporate-workshops-page">
      <Navigation isScrolled={isScrolled} />
      <div className="pt-20 sm:pt-24">
        {/* Hero */}
        <div className="relative">
          <div className="absolute inset-0 bg-burgundy/85"></div>
          <img src="https://customer-assets.emergentagent.com/job_db70e33d-7b71-4dc3-aa7e-c570bb7270eb/artifacts/vjo8el1c_IMG_8188.JPG" alt="Corporate Workshop" className="w-full h-[500px] sm:h-[600px] object-cover opacity-40" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-4xl mx-auto px-4 text-center text-white">
              <h1 className="font-h1 text-4xl sm:text-5xl lg:text-6xl mb-4">Corporate Workshops</h1>
              <p className="font-h2 text-xl sm:text-2xl mb-4">Leadership Performance Partnership</p>
              <p className="font-body text-cream-light text-lg mb-8">Build resilient, high-performing teams with evidence-based training</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <span className="font-h1 text-3xl sm:text-4xl">CI$2,000</span>
                <span className="font-body text-cream-light">per 2-hour session</span>
              </div>
              <button onClick={() => setShowEnrollModal(true)} className="inline-block bg-white text-burgundy px-8 py-4 rounded-full font-h1 text-lg hover:bg-cream-light transition-colors cursor-pointer">Request a Proposal</button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          {/* Intro */}
          <div className="mb-12 text-center">
            <h2 className="font-h2 text-2xl sm:text-3xl text-burgundy mb-6">Strengthen Performance. Reduce Risk. Retain Top Talent.</h2>
            <p className="font-body text-burgundy/80 text-lg max-w-3xl mx-auto">In high-pressure environments, burnout isn't just a personal issue — it's a business risk. My corporate workshops help leadership teams build the resilience and performance systems they need to thrive without sacrificing wellbeing.</p>
          </div>

          {/* Workshops */}
          <div className="mb-12">
            <h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-8 text-center">Workshop Topics</h3>
            <div className="space-y-6">
              {workshops.map((w, i) => (
                <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="bg-burgundy p-6">
                    <h4 className="font-h2 text-xl sm:text-2xl text-white">{w.title}</h4>
                    <p className="font-body text-cream-light mt-2">{w.desc}</p>
                  </div>
                  <div className="p-6">
                    <p className="font-h2 text-burgundy text-sm mb-3">Participants will:</p>
                    <ul className="grid sm:grid-cols-2 gap-2">
                      {w.outcomes.map((o, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-burgundy mt-0.5 flex-shrink-0" />
                          <span className="font-body text-burgundy/80 text-sm">{o}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 3-Month Partnership */}
          <div className="bg-cream-light border-2 border-burgundy rounded-2xl p-6 sm:p-8 mb-12">
            <h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-4 text-center">3-Month Leadership Performance Partnership</h3>
            <p className="font-body text-burgundy/80 text-center mb-6">For organisations seeking ongoing support, I offer a structured 3-month programme:</p>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="font-h2 text-burgundy">Month 1</p>
                <p className="font-body text-burgundy/70 text-sm">Preventing Burnout in High-Pressure Roles</p>
              </div>
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="font-h2 text-burgundy">Month 2</p>
                <p className="font-body text-burgundy/70 text-sm">Psychological Resilience for Leaders</p>
              </div>
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="font-h2 text-burgundy">Month 3</p>
                <p className="font-body text-burgundy/70 text-sm">Executive Presence for Emerging Leaders</p>
              </div>
            </div>
            <p className="font-body text-burgundy/80 text-center text-sm">Each month includes: 1 x 90-minute workshop + 1 x 60-minute group coaching session + optional 1:1 drop-in slots</p>
            <p className="font-h2 text-burgundy text-center mt-4">Starting at CI$2,500/month</p>
          </div>

          {/* Why Choose */}
          <div className="mb-12">
            <h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-6 text-center">Why Work With Me?</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "15+ years leading high-performing teams",
                "Founded successful recruitment agency in Cayman",
                "Certified Master Coach (NLP, CBT)",
                "Understand Cayman's high-stakes market",
                "Evidence-based, practical approach",
                "Measurable outcomes and ROI focus"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4">
                  <Check className="w-5 h-5 text-burgundy mt-0.5 flex-shrink-0" />
                  <span className="font-body text-burgundy/80">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-burgundy rounded-2xl p-6 sm:p-8 text-center text-white">
            <h3 className="font-h2 text-xl sm:text-2xl mb-4">Ready to Strengthen Your Team?</h3>
            <p className="font-body text-cream-light mb-6">Let's discuss how a customised workshop or partnership can support your organisation's goals.</p>
            <button onClick={() => setShowEnrollModal(true)} className="inline-block bg-white text-burgundy px-8 py-4 rounded-full font-h1 text-lg hover:bg-cream-light transition-colors cursor-pointer">Request a Proposal</button>
          </div>

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link to="/get-started" className="font-body text-burgundy hover:underline flex items-center justify-center gap-2">
              <ChevronLeft className="w-5 h-5" />View All Services
            </Link>
          </div>
        </div>
      </div>
      <EnrollmentModal 
        isOpen={showEnrollModal} 
        onClose={() => setShowEnrollModal(false)} 
        serviceName="Corporate Workshop Enquiry" 
        paymentLink="https://calendly.com/chelseaflynncoaching/purpose-discovery-chat"
      />
      <Footer /><FloatingWhatsApp />
    </div>
  );
};

// Keep old route working - redirect
const SacredSistersPage = () => {
  const navigate = useNavigate();
  useEffect(() => { navigate('/services/corporate-workshops'); }, [navigate]);
  return null;
};

// Breakthrough Session Page
const BreakthroughSessionPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);

  const outcomes = [
    "Gain clarity on a specific challenge, decision, or next step",
    "Identify limiting beliefs or patterns keeping you stuck",
    "Develop actionable strategies to move forward with confidence",
    "Release overwhelm and regain focus",
    "Create a clear action plan you can implement immediately"
  ];

  return (
    <div className="min-h-screen bg-cream-light" data-testid="breakthrough-session-page">
      <Navigation isScrolled={isScrolled} />
      <div className="pt-20 sm:pt-24">
        {/* Hero */}
        <div className="relative">
          <div className="absolute inset-0 bg-burgundy/85"></div>
          <img src="https://customer-assets.emergentagent.com/job_db70e33d-7b71-4dc3-aa7e-c570bb7270eb/artifacts/pjv8apbb_IMG_3765.JPG" alt="Breakthrough Session" className="w-full h-[500px] sm:h-[600px] object-cover opacity-40" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-4xl mx-auto px-4 text-center text-white">
              <h1 className="font-h1 text-4xl sm:text-5xl lg:text-6xl mb-4">Breakthrough Session</h1>
              <p className="font-h2 text-xl sm:text-2xl mb-4">Focused Clarity in 60 Minutes</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <span className="font-h1 text-3xl sm:text-4xl">US$250</span>
                <span className="font-body text-cream-light">One-hour intensive</span>
              </div>
              <button onClick={() => setShowEnrollModal(true)} className="inline-block bg-white text-burgundy px-8 py-4 rounded-full font-h1 text-lg hover:bg-cream-light transition-colors cursor-pointer">Book Your Session</button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
          {/* Intro */}
          <div className="mb-12">
            <h2 className="font-h2 text-2xl sm:text-3xl text-burgundy mb-6 text-center">FAST CLARITY WHEN YOU NEED IT MOST</h2>
            <div className="font-body text-burgundy/80 space-y-4 text-lg leading-relaxed">
              <p>Sometimes you don't need an 8-week programme — you need one focused hour to get clear, release what's weighing you down, and walk away with a practical action plan.</p>
              <p>The Breakthrough Session is designed for ambitious professionals who are facing a specific challenge, decision, or crossroads and need expert guidance to move forward with confidence.</p>
              <p className="font-h2 text-burgundy">This is not surface-level advice — it's deep, strategic coaching that gets results fast.</p>
            </div>
          </div>

          {/* What You'll Get */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-12">
            <h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-6">What You'll Walk Away With</h3>
            <ul className="space-y-4">
              {outcomes.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-burgundy mt-0.5 flex-shrink-0" />
                  <span className="font-body text-burgundy/80 text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Ideal For */}
          <div className="bg-cream-light border-2 border-burgundy rounded-2xl p-6 sm:p-8 mb-12">
            <h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-4 text-center">Ideal For</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Career decisions or transitions",
                "Leadership challenges",
                "Navigating difficult conversations",
                "Imposter syndrome or confidence blocks",
                "Burnout prevention strategies",
                "Work-life balance decisions"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-3">
                  <Check className="w-5 h-5 text-burgundy flex-shrink-0" />
                  <span className="font-body text-burgundy/80">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-burgundy rounded-2xl p-6 sm:p-8 text-center text-white">
            <h3 className="font-h2 text-xl sm:text-2xl mb-4">Ready for Your Breakthrough?</h3>
            <div className="font-h1 text-3xl sm:text-4xl mb-4">US$250</div>
            <p className="font-body text-cream-light mb-6">60 minutes of focused, strategic coaching</p>
            <button onClick={() => setShowEnrollModal(true)} className="inline-block bg-white text-burgundy px-8 py-4 rounded-full font-h1 text-lg hover:bg-cream-light transition-colors cursor-pointer">Book Your Session</button>
          </div>

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link to="/get-started" className="font-body text-burgundy hover:underline flex items-center justify-center gap-2">
              <ChevronLeft className="w-5 h-5" />View All Services
            </Link>
          </div>
        </div>
      </div>
      <EnrollmentModal 
        isOpen={showEnrollModal} 
        onClose={() => setShowEnrollModal(false)} 
        serviceName="Breakthrough Session" 
        paymentLink="https://calendly.com/chelseaflynncoaching/purpose-discovery-chat"
      />
      <Footer /><FloatingWhatsApp />
    </div>
  );
};

// Speaking Engagements Page
const SpeakingEngagementsPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);

  const outcomes = [
    "Evidence-based strategies for preventing burnout and building resilience",
    "Practical tools for emotional regulation and stress management",
    "Techniques for developing executive presence and authentic confidence",
    "Actionable frameworks your team can implement immediately",
    "An engaging, memorable experience that drives real behaviour change"
  ];

  const topics = [
    "Preventing Burnout in High-Pressure Roles",
    "Psychological Resilience for Leadership Teams",
    "Confidence & Executive Presence for Emerging Leaders",
    "The Whole Human Method: Sustainable High Performance",
    "From Overwhelmed to Unstoppable: Managing Stress at the Top",
    "Building Resilient Teams in Relentless Environments",
    "The Neuroscience of Leadership Performance",
    "Breaking Through Imposter Syndrome",
    "Work-Life Integration for Ambitious Professionals"
  ];

  const pricing = [
    { type: "Corporate workshops (2 hours)", range: "CI$2,000" },
    { type: "Conference keynotes", range: "CI$2,500-$5,000 + expenses" },
    { type: "Half-day leadership intensives", range: "CI$4,000-$6,000" },
    { type: "Virtual sessions", range: "CI$1,500-$2,500" }
  ];

  return (
    <div className="min-h-screen bg-cream-light" data-testid="speaking-page">
      <Navigation isScrolled={isScrolled} />
      <div className="pt-20 sm:pt-24">
        {/* Hero Section */}
        <div className="bg-burgundy py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <h1 className="font-h1 text-4xl sm:text-5xl lg:text-6xl mb-4">Speaking & Workshops</h1>
            <p className="font-h2 text-xl sm:text-2xl lg:text-3xl mb-2">Leadership Performance & Resilience Training</p>
            <p className="font-body text-cream-light mt-4 text-base sm:text-lg mb-6">Keynotes • Corporate Workshops • Team Training</p>
            <button onClick={() => setShowEnrollModal(true)} className="inline-block bg-white text-burgundy px-8 py-4 rounded-full font-h1 text-lg hover:bg-cream-light transition-colors cursor-pointer mb-10" data-testid="speaking-inquire-top">Request a Proposal</button>
            {/* Image instead of video */}
            <div className="relative w-full max-w-2xl mx-auto aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_db70e33d-7b71-4dc3-aa7e-c570bb7270eb/artifacts/vjo8el1c_IMG_8188.JPG"
                alt="Chelsea Flynn Speaking"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
          {/* Intro */}
          <div className="mb-12">
            <h2 className="font-h2 text-2xl sm:text-3xl lg:text-4xl text-burgundy mb-6 text-center">ELEVATE YOUR TEAM'S PERFORMANCE</h2>
            <div className="font-body text-burgundy/80 space-y-4 text-base sm:text-lg leading-relaxed">
              <p>In high-pressure environments, your people are your greatest asset — and your biggest risk. Burnout, stress, and disengagement cost organisations millions in lost productivity, turnover, and missed opportunities.</p>
              <p>With 15+ years leading high-performing teams, experience founding a successful recruitment agency, and a background as a Master Coach specialising in neuroscience-based performance strategies, I deliver workshops and keynotes that create real, measurable change.</p>
              <p className="font-h2 text-burgundy text-lg sm:text-xl">My talks blend practical strategies, engaging delivery, and evidence-based frameworks that your team can implement immediately.</p>
            </div>
          </div>

          {/* What You Can Expect */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-12">
            <h3 className="font-h2 text-xl sm:text-2xl lg:text-3xl text-burgundy mb-6">What Your Organisation Gets</h3>
            <p className="font-body text-burgundy/70 mb-6 text-base sm:text-lg">Every engagement is tailored to your audience and objectives. Common outcomes include:</p>
            <ul className="space-y-4">
              {outcomes.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-burgundy mt-0.5 flex-shrink-0" />
                  <span className="font-body text-burgundy/80 text-base sm:text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Speaking Topics */}
          <div className="bg-burgundy/10 rounded-2xl p-6 sm:p-8 mb-12">
            <h3 className="font-h2 text-xl sm:text-2xl lg:text-3xl text-burgundy mb-6">Popular Topics (Customisable)</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {topics.map((topic, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Mic className="w-6 h-6 text-burgundy mt-0.5 flex-shrink-0" />
                  <span className="font-body text-burgundy/80 text-base sm:text-lg">{topic}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Investment Section */}
          <div className="bg-burgundy rounded-2xl p-6 sm:p-8 text-center text-white mb-12">
            <h3 className="font-h2 text-xl sm:text-2xl lg:text-3xl mb-6">Investment</h3>
            <p className="font-body text-cream-light mb-6 text-base sm:text-lg">Pricing depends on format, duration, travel, and audience size.</p>
            <div className="space-y-3 max-w-md mx-auto mb-8">
              {pricing.map((item, i) => (
                <div key={i} className="flex justify-between items-center bg-white/10 rounded-lg px-4 py-3">
                  <span className="font-body text-cream-light text-base">{item.type}</span>
                  <span className="font-h2 text-white text-base">{item.range}</span>
                </div>
              ))}
            </div>
            <p className="font-body text-cream-light text-base sm:text-lg mb-8">Every engagement is customised to deliver measurable outcomes for your organisation.</p>
            <button onClick={() => setShowEnrollModal(true)} className="inline-block bg-white text-burgundy px-8 py-4 rounded-full font-h1 text-lg hover:bg-cream-light transition-colors cursor-pointer" data-testid="speaking-inquire">Request a Proposal</button>
          </div>

          {/* Back Link */}
          <div className="text-center">
            <Link to="/get-started" className="font-body text-burgundy hover:underline flex items-center justify-center gap-2 text-base sm:text-lg">
              <ChevronLeft className="w-5 h-5" />View All Services
            </Link>
          </div>
        </div>
      </div>
      <EnrollmentModal 
        isOpen={showEnrollModal} 
        onClose={() => setShowEnrollModal(false)} 
        serviceName="Speaking Engagement" 
        paymentLink={null}
      />
      <Footer /><FloatingWhatsApp />
    </div>
  );
};

// Free Guide Lead Capture Page
const FreeGuidePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', instagram: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/leads`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        instagram: formData.instagram,
        lead_source: 'purpose-worksheet'
      });
      // Store name and email for the worksheet page
      localStorage.setItem('worksheet_name', `${formData.firstName} ${formData.lastName}`);
      localStorage.setItem('worksheet_email', formData.email);
      navigate('/free-guide/purpose-worksheet');
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-light" data-testid="free-guide-page">
      <Navigation isScrolled={isScrolled} />
      <div className="pt-20 sm:pt-24">
        {/* Hero */}
        <div className="bg-burgundy py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <p className="font-body text-cream-light text-base sm:text-lg mb-4 uppercase tracking-wider">Free Resource</p>
            <h1 className="font-h1 text-3xl sm:text-5xl lg:text-6xl mb-6">Finding Your God-Given Purpose</h1>
            <p className="font-h2 text-xl sm:text-2xl text-cream-light">A Reflection Worksheet for Women Ready to Discover Their Calling</p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left - Benefits */}
            <div>
              <h2 className="font-h2 text-2xl sm:text-3xl text-burgundy mb-6">What You'll Discover</h2>
              <div className="space-y-4">
                {[
                  "Clarity on what's been holding you back from your calling",
                  "A framework to identify your unique God-given gifts",
                  "Guided questions to uncover your deepest purpose",
                  "Scripture-based affirmations for your journey",
                  "Practical next steps to start living with intention"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-burgundy mt-0.5 flex-shrink-0" />
                    <span className="font-body text-burgundy/80 text-base sm:text-lg">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-burgundy/10 rounded-2xl">
                <p className="font-body text-burgundy text-base sm:text-lg italic">
                  "I created this worksheet for women like you — women who feel called to something greater but aren't sure where to start. This is the same framework I use with my 1:1 coaching clients."
                </p>
                <p className="font-h2 text-burgundy mt-4">— Chelsea Flynn</p>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
              <h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-2 text-center">Get Instant Access</h3>
              <p className="font-body text-burgundy/70 text-center mb-6">Enter your details below and start your journey today.</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body text-burgundy text-sm mb-1">First Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                      placeholder="First name"
                      data-testid="lead-firstname-input"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-burgundy text-sm mb-1">Last Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                      placeholder="Last name"
                      data-testid="lead-lastname-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-body text-burgundy text-sm mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                    placeholder="your@email.com"
                    data-testid="lead-email-input"
                  />
                </div>
                <div>
                  <label className="block font-body text-burgundy text-sm mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                    placeholder="+1 (555) 000-0000"
                    data-testid="lead-phone-input"
                  />
                </div>
                <div>
                  <label className="block font-body text-burgundy text-sm mb-1">Instagram Handle *</label>
                  <input
                    type="text"
                    required
                    value={formData.instagram}
                    onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                    placeholder="@yourusername"
                    data-testid="lead-instagram-input"
                  />
                </div>
                
                {error && <p className="text-red-600 font-body text-sm">{error}</p>}
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-burgundy text-white py-4 rounded-full font-h1 text-base sm:text-lg hover:bg-burgundy/90 transition-colors disabled:opacity-50"
                  data-testid="lead-submit-btn"
                >
                  {loading ? 'Please wait...' : 'Access My Purpose Worksheet'}
                </button>
                
                <p className="font-body text-burgundy/50 text-xs text-center">
                  Your information is safe. We respect your privacy and will never spam you.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer /><FloatingWhatsApp />
    </div>
  );
};

// ==================== STRESS RESET TOOLKIT (LinkedIn Lead Magnet) ====================

const StressResetPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/leads`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        instagram: null,
        lead_source: 'stress-reset-toolkit'
      });
      localStorage.setItem('toolkit_name', `${formData.firstName} ${formData.lastName}`);
      localStorage.setItem('toolkit_email', formData.email);
      navigate('/stress-reset/toolkit');
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-light" data-testid="stress-reset-page">
      <Navigation isScrolled={isScrolled} />
      <div className="pt-20 sm:pt-24">
        {/* Hero */}
        <div className="bg-burgundy py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <img 
              src="https://customer-assets.emergentagent.com/job_db70e33d-7b71-4dc3-aa7e-c570bb7270eb/artifacts/8ihmi9th_20250913-YAN02723.jpg" 
              alt="Chelsea Flynn" 
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto object-cover border-4 border-white/20 shadow-lg mb-4"
            />
            <p className="font-body text-cream-light text-base sm:text-lg mb-3 uppercase tracking-wider">Free Toolkit</p>
            <h1 className="font-h1 text-3xl sm:text-5xl lg:text-6xl mb-4">The Stress Reset Toolkit</h1>
            <p className="font-h2 text-xl sm:text-2xl text-cream-light">3 Neuroscience-Backed Exercises to Calm Your Nervous System Today</p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left - Benefits */}
            <div>
              <h2 className="font-h2 text-2xl sm:text-3xl text-burgundy mb-6">What You'll Learn</h2>
              <div className="space-y-4">
                {[
                  "The 90-second reset technique to interrupt stress in the moment",
                  "A breathing pattern that activates your body's calm response",
                  "The grounding exercise used in CBT and trauma therapy",
                  "Why these work (the neuroscience made simple)",
                  "How to build these into your daily routine"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-burgundy mt-0.5 flex-shrink-0" />
                    <span className="font-body text-burgundy/80 text-base sm:text-lg">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-burgundy/10 rounded-2xl">
                <p className="font-body text-burgundy text-base sm:text-lg italic">
                  "These are the exact techniques I teach my coaching clients and in my corporate workshops. They're simple, backed by science, and they work."
                </p>
                <p className="font-h2 text-burgundy mt-4">— Chelsea Flynn</p>
              </div>

              {/* Who It's For */}
              <div className="mt-8 space-y-3">
                <h3 className="font-h2 text-lg text-burgundy">Perfect for:</h3>
                <div className="flex flex-wrap gap-2">
                  {["Busy professionals", "Leaders & managers", "Anyone feeling overwhelmed", "Corporate teams"].map((tag, i) => (
                    <span key={i} className="bg-burgundy/10 text-burgundy px-3 py-1 rounded-full font-body text-sm">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
              <h3 className="font-h2 text-xl sm:text-2xl text-burgundy mb-2 text-center">Get Instant Access</h3>
              <p className="font-body text-burgundy/70 text-center mb-6">Enter your details and start resetting your stress today.</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body text-burgundy text-sm mb-1">First Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                      placeholder="First name"
                      data-testid="stress-firstname"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-burgundy text-sm mb-1">Last Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                      placeholder="Last name"
                      data-testid="stress-lastname"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-body text-burgundy text-sm mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                    placeholder="Email address"
                    data-testid="stress-email"
                  />
                </div>
                <div>
                  <label className="block font-body text-burgundy text-sm mb-1">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                    placeholder="Phone number"
                    data-testid="stress-phone"
                  />
                </div>
                
                {error && <p className="text-red-600 font-body text-sm text-center">{error}</p>}
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-burgundy text-white py-4 rounded-full font-h1 text-lg hover:bg-burgundy/90 transition-colors disabled:opacity-50"
                  data-testid="stress-submit"
                >
                  {loading ? 'Please wait...' : 'Get My Free Toolkit →'}
                </button>
                
                <p className="font-body text-burgundy/50 text-xs text-center">
                  Your information is safe and will never be shared.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const StressResetToolkitPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentExercise, setCurrentExercise] = useState(0);
  const [completedExercises, setCompletedExercises] = useState([]);
  const navigate = useNavigate();
  
  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);

  const userName = localStorage.getItem('toolkit_name') || '';

  const exercises = [
    {
      id: 1,
      title: "The 90-Second Reset",
      subtitle: "Interrupt your stress response in real-time",
      icon: "⚡",
      time: "90 seconds",
      science: "When stress hits, your amygdala triggers a cascade of hormones. But here's the secret: that chemical surge only lasts about 90 seconds — unless you keep feeding it with your thoughts.",
      steps: [
        "Notice the stress sensation in your body (tight chest, racing heart, tension)",
        "Name it: \"I notice I'm feeling stressed right now\"",
        "Set a mental timer for 90 seconds",
        "Focus only on the physical sensation — don't engage with the thoughts",
        "Breathe slowly and let the wave pass through you"
      ],
      tip: "The key is observation without judgment. You're not trying to fix it — you're letting the chemical response complete its natural cycle."
    },
    {
      id: 2,
      title: "Box Breathing",
      subtitle: "Activate your parasympathetic nervous system",
      icon: "◻️",
      time: "2-4 minutes",
      science: "This technique is used by Navy SEALs and elite athletes. Extended exhales activate your vagus nerve, which switches your nervous system from 'fight or flight' to 'rest and digest.'",
      steps: [
        "Breathe IN for 4 counts (through your nose)",
        "HOLD for 4 counts",
        "Breathe OUT for 4 counts (through your mouth)",
        "HOLD for 4 counts",
        "Repeat 4-6 times"
      ],
      tip: "If 4 counts feels too long, start with 3. The extended exhale is the most important part — that's what tells your brain you're safe."
    },
    {
      id: 3,
      title: "5-4-3-2-1 Grounding",
      subtitle: "Anchor yourself in the present moment",
      icon: "✋",
      time: "2-3 minutes",
      science: "Anxiety lives in the future; regret lives in the past. This CBT technique forces your brain into the present moment by engaging all five senses, interrupting rumination and worry loops.",
      steps: [
        "Name 5 things you can SEE (look around slowly)",
        "Name 4 things you can TOUCH (feel textures around you)",
        "Name 3 things you can HEAR (listen carefully)",
        "Name 2 things you can SMELL (or imagine comforting scents)",
        "Name 1 thing you can TASTE (or take a sip of water)"
      ],
      tip: "Speak out loud if you can — it engages more of your brain and makes it more effective. Great for moments of panic or overwhelm."
    }
  ];

  const markComplete = (index) => {
    if (!completedExercises.includes(index)) {
      setCompletedExercises([...completedExercises, index]);
    }
  };

  const goToNextExercise = () => {
    setCurrentExercise(currentExercise + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const allComplete = completedExercises.length === exercises.length;

  return (
    <div className="min-h-screen bg-cream-light" data-testid="stress-toolkit-page">
      <Navigation isScrolled={isScrolled} />
      <div className="pt-20 sm:pt-24">
        {/* Hero */}
        <div className="bg-burgundy py-10 sm:py-12">
          <div className="max-w-3xl mx-auto px-4 text-center text-white">
            <p className="font-body text-cream-light text-sm mb-2">Welcome{userName ? `, ${userName.split(' ')[0]}` : ''}!</p>
            <h1 className="font-h1 text-2xl sm:text-4xl mb-3">Your Stress Reset Toolkit</h1>
            <p className="font-body text-cream-light text-base">3 exercises. Backed by neuroscience. Ready to use right now.</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white border-b border-burgundy/10 py-4 sticky top-16 sm:top-20 z-40">
          <div className="max-w-3xl mx-auto px-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-body text-burgundy text-sm">{completedExercises.length} of 3 complete</span>
              {allComplete && <span className="text-green-600 font-h2 text-sm">✓ All done!</span>}
            </div>
            <div className="h-2 bg-burgundy/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-burgundy transition-all duration-500"
                style={{ width: `${(completedExercises.length / 3) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Exercise Navigation */}
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex gap-2 sm:gap-4 justify-center mb-8">
            {exercises.map((ex, i) => (
              <button
                key={i}
                onClick={() => { setCurrentExercise(i); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`flex-1 max-w-[140px] p-3 sm:p-4 rounded-xl transition-all ${
                  currentExercise === i 
                    ? 'bg-burgundy text-white shadow-lg' 
                    : completedExercises.includes(i)
                    ? 'bg-green-100 text-green-800 border-2 border-green-300'
                    : 'bg-white text-burgundy border-2 border-burgundy/20 hover:border-burgundy'
                }`}
              >
                <div className="text-2xl mb-1">{ex.icon}</div>
                <div className="font-h2 text-xs sm:text-sm leading-tight">
                  {completedExercises.includes(i) && '✓ '}{ex.title.split(' ').slice(-1)}
                </div>
              </button>
            ))}
          </div>

          {/* Current Exercise */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-burgundy/5 p-6 border-b border-burgundy/10">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">{exercises[currentExercise].icon}</span>
                <div>
                  <h2 className="font-h1 text-xl sm:text-2xl text-burgundy">{exercises[currentExercise].title}</h2>
                  <p className="font-body text-burgundy/70 text-sm">{exercises[currentExercise].subtitle}</p>
                </div>
              </div>
              <span className="inline-block bg-burgundy/10 text-burgundy px-3 py-1 rounded-full font-body text-xs">
                ⏱ {exercises[currentExercise].time}
              </span>
            </div>

            <div className="p-6 space-y-6">
              {/* The Science */}
              <div className="bg-cream-light rounded-xl p-4">
                <h3 className="font-h2 text-burgundy text-sm mb-2 flex items-center gap-2">
                  <span>🧠</span> The Science
                </h3>
                <p className="font-body text-burgundy/80 text-sm leading-relaxed">
                  {exercises[currentExercise].science}
                </p>
              </div>

              {/* Steps */}
              <div>
                <h3 className="font-h2 text-burgundy text-sm mb-3">How to do it:</h3>
                <div className="space-y-3">
                  {exercises[currentExercise].steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-burgundy text-white rounded-full flex items-center justify-center font-h2 text-xs flex-shrink-0">
                        {i + 1}
                      </span>
                      <p className="font-body text-burgundy/80 text-sm sm:text-base">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pro Tip */}
              <div className="bg-burgundy/5 rounded-xl p-4 border-l-4 border-burgundy">
                <h3 className="font-h2 text-burgundy text-sm mb-1">💡 Pro Tip</h3>
                <p className="font-body text-burgundy/80 text-sm">{exercises[currentExercise].tip}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  onClick={() => markComplete(currentExercise)}
                  disabled={completedExercises.includes(currentExercise)}
                  className={`flex-1 py-3 rounded-full font-h2 text-base transition-all ${
                    completedExercises.includes(currentExercise)
                      ? 'bg-green-100 text-green-800 cursor-default'
                      : 'bg-burgundy text-white hover:bg-burgundy/90'
                  }`}
                >
                  {completedExercises.includes(currentExercise) ? '✓ Completed' : 'Mark as Complete'}
                </button>
                {currentExercise < 2 && (
                  <button
                    onClick={goToNextExercise}
                    className="flex-1 py-3 rounded-full font-h2 text-base border-2 border-burgundy text-burgundy hover:bg-burgundy hover:text-white transition-all"
                  >
                    Next Exercise →
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* CTA Section - Always visible, highlighted when complete */}
          <div className={`mt-8 rounded-2xl p-6 sm:p-8 text-center transition-all ${
            allComplete ? 'bg-burgundy text-white' : 'bg-white border-2 border-burgundy/20'
          }`}>
            {allComplete ? (
              <>
                <div className="text-4xl mb-3">🎉</div>
                <h2 className="font-h1 text-xl sm:text-2xl mb-3">You've Completed the Toolkit!</h2>
                <p className="font-body text-cream-light text-base mb-6 max-w-lg mx-auto">
                  These exercises are just the beginning. Imagine having personalized strategies, ongoing support, and a complete system for managing stress long-term.
                </p>
              </>
            ) : (
              <>
                <h2 className="font-h1 text-xl sm:text-2xl text-burgundy mb-3">Want to Go Deeper?</h2>
                <p className="font-body text-burgundy/70 text-base mb-6 max-w-lg mx-auto">
                  These exercises are powerful — but they're just the start. Let's talk about building a complete stress management system for your life.
                </p>
              </>
            )}
            
            <a 
              href="https://calendly.com/chelseaflynncoaching/purpose-discovery-chat" 
              target="_blank" 
              rel="noopener noreferrer"
              className={`inline-block px-8 py-4 rounded-full font-h1 text-lg transition-colors ${
                allComplete 
                  ? 'bg-white text-burgundy hover:bg-cream-light' 
                  : 'bg-burgundy text-white hover:bg-burgundy/90'
              }`}
            >
              Book a Free Discovery Call
            </a>
            
            <p className={`font-body text-sm mt-4 ${allComplete ? 'text-cream-light/70' : 'text-burgundy/50'}`}>
              No pressure — just a conversation about what's possible for you.
            </p>
          </div>

          {/* Corporate CTA */}
          <div className="mt-6 bg-cream-light border border-burgundy/20 rounded-xl p-6 text-center">
            <h3 className="font-h2 text-burgundy text-lg mb-2">Looking for Corporate Training?</h3>
            <p className="font-body text-burgundy/70 text-sm mb-4">
              I deliver workshops on stress management, resilience, and mindset for teams and organizations.
            </p>
            <Link 
              to="/services/speaking"
              className="inline-block text-burgundy font-h2 text-sm underline hover:text-burgundy/70"
            >
              Learn about Speaking Engagements →
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// General Lead Capture Page (for ManyChat / general inquiries)
const StartPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', instagram: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/leads`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        instagram: formData.instagram,
        lead_source: 'general-inquiry'
      });
      navigate('/get-started');
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-light" data-testid="start-page">
      <Navigation isScrolled={isScrolled} />
      <div className="pt-20 sm:pt-24">
        {/* Hero */}
        <div className="bg-burgundy py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <h1 className="font-h1 text-3xl sm:text-5xl mb-4">TRANSFORM</h1>
            <p className="font-h2 text-xl sm:text-2xl text-cream-light">Let's get you started on your transformation journey</p>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-md mx-auto px-4 py-12 sm:py-16">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <p className="font-body text-burgundy/70 text-center mb-6">Enter your details to view our coaching services</p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                    placeholder="First name *"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                    placeholder="Last name *"
                  />
                </div>
              </div>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                placeholder="Email address *"
              />
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                placeholder="Phone number *"
              />
              <input
                type="text"
                required
                value={formData.instagram}
                onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy"
                placeholder="Instagram handle *"
              />
              
              {error && <p className="text-red-600 font-body text-sm text-center">{error}</p>}
              
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-burgundy text-white py-4 rounded-full font-h1 text-lg hover:bg-burgundy/90 transition-colors disabled:opacity-50"
              >
                {loading ? 'Please wait...' : 'View Services →'}
              </button>
              
              <p className="font-body text-burgundy/50 text-xs text-center">
                Your information is safe and will never be shared.
              </p>
            </form>
          </div>
        </div>
      </div>
      <Footer /><FloatingWhatsApp />
    </div>
  );
};

// Purpose Worksheet Guide Page
const PurposeWorksheetPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [responses, setResponses] = useState({});
  const [sending, setSending] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  useEffect(() => { const h = () => setIsScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);

  // Get the user's name and email from localStorage (captured on /free-guide page)
  const userName = localStorage.getItem('worksheet_name') || '';
  const userEmail = localStorage.getItem('worksheet_email') || '';

  const sections = [
    {
      title: "Part 1: Your Story & Gifts",
      icon: "✨",
      intro: "God has been writing your journey — and uniquely equipped you for a purpose.",
      questions: [
        { id: "q1", text: "What experience or struggle in your life could help someone else going through the same thing?" },
        { id: "q2", text: "What do people often come to you for help with, or compliment you on?" }
      ],
      scripture: "\"For we are God's handiwork, created in Christ Jesus to do good works, which God prepared in advance for us to do.\" — Ephesians 2:10"
    },
    {
      title: "Part 2: Your Calling",
      icon: "🔥",
      intro: "Your calling lives at the intersection of your gifts, your passions, and the world's needs.",
      questions: [
        { id: "q3", text: "What breaks your heart when you see it in the world? What makes you want to take action?" },
        { id: "q4", text: "If you knew you couldn't fail, what would you do to serve God and others?" }
      ],
      scripture: "\"For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future.\" — Jeremiah 29:11"
    },
    {
      title: "Part 3: Your Next Step",
      icon: "👣",
      intro: "Purpose isn't discovered all at once — it's revealed one step at a time.",
      questions: [
        { id: "q5", text: "What fear or limiting belief has been holding you back from stepping into your calling?" },
        { id: "q6", text: "What is ONE small step you can take this week to move toward your purpose?" }
      ],
      scripture: "\"Trust in the Lord with all your heart and lean not on your own understanding; in all your ways submit to him, and he will make your paths straight.\" — Proverbs 3:5-6"
    }
  ];

  const handleSubmit = async () => {
    // Check if at least one answer is provided
    const hasAnswers = Object.values(responses).some(a => a && a.trim());
    if (!hasAnswers) {
      setError('Please answer at least one question before submitting.');
      return;
    }

    if (!userEmail) {
      setError('Email not found. Please go back to the guide page and enter your details.');
      return;
    }

    setSending(true);
    setError('');

    try {
      await axios.post(`${API}/worksheet/complete`, {
        name: userName,
        email: userEmail,
        answers: responses
      });
      setCompleted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setSending(false);
    }
  };

  const questionLabels = {
    q1: "What experience or struggle in your life could help someone else going through the same thing?",
    q2: "What do people often come to you for help with, or compliment you on?",
    q3: "What breaks your heart when you see it in the world? What makes you want to take action?",
    q4: "If you knew you couldn't fail, what would you do to serve God and others?",
    q5: "What fear or limiting belief has been holding you back from stepping into your calling?",
    q6: "What is ONE small step you can take this week to move toward your purpose?"
  };

  // Show Thank You page after completion
  if (completed) {
    return (
      <div className="min-h-screen bg-cream-light" data-testid="worksheet-thankyou-page">
        <Navigation isScrolled={isScrolled} />
        <div className="pt-20 sm:pt-24">
          <div className="bg-burgundy py-12 sm:py-16">
            <div className="max-w-3xl mx-auto px-4 text-center text-white">
              <div className="text-5xl mb-4">✨</div>
              <h1 className="font-h1 text-3xl sm:text-4xl mb-3">You Did It, {userName.split(' ')[0]}!</h1>
              <p className="font-body text-cream-light text-base sm:text-lg">
                Your reflections have been saved. Scroll down to see your responses.
              </p>
            </div>
          </div>

          {/* Your Responses Section */}
          <div className="max-w-3xl mx-auto px-4 py-10 sm:py-12">
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-h1 text-xl sm:text-2xl text-burgundy">Your Purpose Worksheet Responses</h2>
                <button 
                  onClick={() => window.print()} 
                  className="text-burgundy/70 hover:text-burgundy text-sm font-body flex items-center gap-1"
                  data-testid="print-responses-btn"
                >
                  <FileText className="w-4 h-4" /> Save/Print
                </button>
              </div>
              
              <div className="space-y-6">
                {Object.entries(questionLabels).map(([qId, question]) => (
                  <div key={qId} className="border-l-4 border-burgundy pl-4 py-2">
                    <p className="font-h2 text-burgundy text-sm sm:text-base mb-2">{question}</p>
                    <p className="font-body text-burgundy/80 text-base whitespace-pre-wrap">
                      {responses[qId] || <span className="italic text-burgundy/50">Not answered</span>}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-burgundy rounded-2xl p-6 sm:p-8 text-center text-white mb-8">
              <h2 className="font-h1 text-2xl sm:text-3xl mb-3">Ready for the Next Step?</h2>
              <p className="font-body text-cream-light text-base sm:text-lg mb-6 max-w-lg mx-auto">
                You've reflected on your purpose — now let's talk about where God is leading you. 
                Book a <strong>free Purpose Discovery Call</strong> with me.
              </p>
              
              <a 
                href="https://calendly.com/chelseaflynncoaching/purpose-discovery-chat" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-white text-burgundy px-10 py-4 rounded-full font-h1 text-lg hover:bg-cream-light transition-colors"
                data-testid="book-discovery-call-btn"
              >
                Book Your Free Discovery Call
              </a>
              
              <p className="font-body text-cream-light/70 text-sm mt-4">
                No pressure, no sales pitch — just a conversation about your calling.
              </p>
            </div>

            <div className="text-center">
              <p className="font-body text-burgundy/70 text-base mb-3">Not ready to chat yet? No problem.</p>
              <Link 
                to="/services/life-transformation" 
                className="inline-block text-burgundy font-h2 underline hover:text-burgundy/70"
              >
                Learn more about 1:1 Coaching →
              </Link>
            </div>
          </div>
        </div>
        <Footer /><FloatingWhatsApp />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-light" data-testid="purpose-worksheet-page">
      <Navigation isScrolled={isScrolled} />
      <div className="pt-20 sm:pt-24">
        {/* Hero */}
        <div className="bg-burgundy py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <p className="font-body text-cream-light text-sm mb-2">✨ Your free resource is ready!</p>
            <h1 className="font-h1 text-3xl sm:text-5xl mb-4">Finding Your God-Given Purpose</h1>
            <p className="font-h2 text-lg sm:text-xl text-cream-light">A Reflection Worksheet</p>
          </div>
        </div>

        {/* Intro */}
        <div className="max-w-3xl mx-auto px-4 py-10 text-center">
          <p className="font-body text-burgundy/80 text-base sm:text-lg leading-relaxed mb-4">
            Just 6 quick questions to help you uncover your God-given purpose.
          </p>
          <p className="font-h2 text-burgundy text-lg sm:text-xl">
            Answer honestly — there's no right or wrong.
          </p>
        </div>

        {/* Sections */}
        <div className="max-w-3xl mx-auto px-4 pb-16">
          {sections.map((section, sIndex) => (
            <div key={sIndex} className="mb-12 bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Section Header */}
              <div className="bg-burgundy p-6 sm:p-8">
                <div className="text-4xl mb-3">{section.icon}</div>
                <h2 className="font-h1 text-2xl sm:text-3xl text-white mb-2">{section.title}</h2>
                <p className="font-body text-cream-light text-base sm:text-lg">{section.intro}</p>
              </div>
              
              {/* Questions */}
              <div className="p-6 sm:p-8 space-y-6">
                {section.questions.map((q, qIndex) => (
                  <div key={q.id}>
                    <label className="block font-h2 text-burgundy text-base sm:text-lg mb-3">
                      {qIndex + 1}. {q.text}
                    </label>
                    <textarea
                      rows={4}
                      value={responses[q.id] || ''}
                      onChange={(e) => setResponses({...responses, [q.id]: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy resize-none"
                      placeholder="Write your reflection here..."
                      data-testid={`worksheet-${q.id}`}
                    />
                  </div>
                ))}
                
                {/* Scripture */}
                <div className="bg-burgundy/10 rounded-xl p-4 sm:p-6 mt-6">
                  <p className="font-body text-burgundy text-base sm:text-lg italic text-center">
                    {section.scripture}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Submit Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
            <div className="text-center">
              <h3 className="font-h1 text-2xl sm:text-3xl text-burgundy mb-4">Ready to Save Your Reflections?</h3>
              <p className="font-body text-burgundy/70 text-base sm:text-lg mb-6">
                Click below to complete your worksheet and see all your responses on one page.
              </p>
              
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg mb-4 font-body text-sm">
                  {error}
                </div>
              )}
              
              <button
                onClick={handleSubmit}
                disabled={sending}
                className="bg-burgundy text-white px-10 py-4 rounded-full font-h1 text-lg hover:bg-burgundy/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-worksheet-btn"
              >
                {sending ? 'Saving...' : 'Complete My Worksheet'}
              </button>
              
              <p className="font-body text-burgundy/50 text-sm mt-4">
                Your responses will be saved for: <strong>{userEmail || 'your email'}</strong>
              </p>
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-burgundy rounded-2xl p-6 sm:p-8 text-center text-white">
            <h3 className="font-h1 text-2xl sm:text-3xl mb-4">Ready to Go Deeper?</h3>
            <p className="font-body text-cream-light text-base sm:text-lg mb-6">
              This worksheet is just the beginning. If you're ready for guided, faith-centered transformation with personalized support, I'd love to walk alongside you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://calendly.com/chelseaflynncoaching/purpose-discovery-chat" target="_blank" rel="noopener noreferrer" className="inline-block bg-white text-burgundy px-8 py-4 rounded-full font-h1 text-lg hover:bg-cream-light transition-colors" data-testid="discovery-call-btn">
                Book a Free Discovery Call
              </a>
              <Link to="/services/life-transformation" className="inline-block bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-h1 text-lg hover:bg-white/10 transition-colors">
                Explore 1:1 Coaching
              </Link>
            </div>
          </div>

          {/* Print/Save Note */}
          <div className="mt-8 text-center">
            <p className="font-body text-burgundy/60 text-sm">
              💡 Tip: Bookmark this page to return to your reflections anytime, or use Ctrl+P (Cmd+P on Mac) to save as PDF.
            </p>
          </div>
        </div>
      </div>
      <Footer /><FloatingWhatsApp />
    </div>
  );
};

// Admin Leads Dashboard
const LeadsDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [filter, setFilter] = useState('all');

  const authenticate = () => {
    if (adminKey === 'chelsea-admin-2024') {
      setAuthenticated(true);
      localStorage.setItem('adminKey', adminKey);
    } else {
      alert('Invalid admin key');
    }
  };

  useEffect(() => {
    const savedKey = localStorage.getItem('adminKey');
    if (savedKey === 'chelsea-admin-2024') {
      setAdminKey(savedKey);
      setAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (authenticated) {
      fetchLeads();
      fetchNotifications();
    }
  }, [authenticated]);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads/public?admin_key=chelsea-admin-2024`);
      setLeads(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch leads', err);
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications?admin_key=chelsea-admin-2024`);
      setNotifications(res.data);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  };

  const markAsRead = async (id) => {
    await axios.put(`${API}/notifications/${id}/read?admin_key=chelsea-admin-2024`);
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const getSourceLabel = (source) => {
    if (source?.includes('purpose-worksheet')) return '📝 Worksheet';
    if (source?.includes('stress-reset')) return '🧘 Stress Reset';
    if (source?.includes('enrollment')) return '💳 Enrollment';
    if (source?.includes('app-transform')) return '📱 Transform App';
    if (source?.includes('ad-discovery-call')) return '📣 Ad - Discovery';
    if (source?.includes('general-inquiry')) return '📋 General Inquiry';
    return '📋 Lead Form';
  };

  const getSourceColor = (source) => {
    if (source?.includes('purpose-worksheet')) return 'bg-green-100 text-green-800';
    if (source?.includes('stress-reset')) return 'bg-teal-100 text-teal-800';
    if (source?.includes('enrollment')) return 'bg-purple-100 text-purple-800';
    if (source?.includes('app-transform')) return 'bg-pink-100 text-pink-800';
    if (source?.includes('ad-discovery-call')) return 'bg-orange-100 text-orange-800';
    if (source?.includes('general-inquiry')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-blue-100 text-blue-800';
  };

  const filteredLeads = filter === 'all' ? leads : leads.filter(l => l.lead_source?.includes(filter));

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Instagram', 'Gender', 'Source', 'Date'];
    const rows = filteredLeads.map(l => [
      l.name, l.email, l.phone || '', l.instagram || '', l.gender || '', l.lead_source, new Date(l.created_at).toLocaleDateString()
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-cream-light flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <h1 className="font-h1 text-2xl text-burgundy mb-6 text-center">Admin Access</h1>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Enter admin key"
            className="w-full px-4 py-3 rounded-lg border border-burgundy/30 mb-4 font-body"
            onKeyPress={(e) => e.key === 'Enter' && authenticate()}
          />
          <button onClick={authenticate} className="w-full bg-burgundy text-white py-3 rounded-full font-h1">
            Access Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-light" data-testid="leads-dashboard">
      <nav className="bg-burgundy py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="font-h1 text-2xl text-white">Leads Dashboard</h1>
          <Link to="/" className="font-body text-cream-light hover:text-white">← Back to Site</Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Notifications */}
        {notifications.length > 0 && (
          <div className="mb-6 bg-white rounded-xl shadow-lg p-4">
            <h2 className="font-h2 text-burgundy mb-3 flex items-center gap-2">
              <Bell className="w-5 h-5" /> New Notifications ({notifications.length})
            </h2>
            <div className="space-y-2">
              {notifications.map(n => (
                <div key={n.id} className="flex justify-between items-center bg-burgundy/5 rounded-lg p-3">
                  <div>
                    <span className="font-h2 text-burgundy">{n.lead_name}</span>
                    <span className="font-body text-burgundy/70 text-sm ml-2">({n.lead_email})</span>
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getSourceColor(n.lead_source)}`}>
                      {getSourceLabel(n.lead_source)}
                    </span>
                  </div>
                  <button onClick={() => markAsRead(n.id)} className="text-burgundy/50 hover:text-burgundy">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="font-h1 text-3xl text-burgundy">{leads.length}</div>
            <div className="font-body text-burgundy/70 text-sm">Total Leads</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="font-h1 text-3xl text-green-600">{leads.filter(l => l.lead_source?.includes('worksheet')).length}</div>
            <div className="font-body text-burgundy/70 text-sm">Worksheet</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="font-h1 text-3xl text-purple-600">{leads.filter(l => l.lead_source?.includes('enrollment')).length}</div>
            <div className="font-body text-burgundy/70 text-sm">Enrollment</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="font-h1 text-3xl text-blue-600">{leads.filter(l => new Date(l.created_at) > new Date(Date.now() - 7*24*60*60*1000)).length}</div>
            <div className="font-body text-burgundy/70 text-sm">This Week</div>
          </div>
        </div>

        {/* Filters & Export */}
        <div className="flex flex-wrap gap-4 mb-6 items-center justify-between">
          <div className="flex gap-2">
            <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-full font-body text-sm ${filter === 'all' ? 'bg-burgundy text-white' : 'bg-white text-burgundy'}`}>All</button>
            <button onClick={() => setFilter('worksheet')} className={`px-4 py-2 rounded-full font-body text-sm ${filter === 'worksheet' ? 'bg-green-600 text-white' : 'bg-white text-green-600'}`}>Worksheet</button>
            <button onClick={() => setFilter('enrollment')} className={`px-4 py-2 rounded-full font-body text-sm ${filter === 'enrollment' ? 'bg-purple-600 text-white' : 'bg-white text-purple-600'}`}>Enrollment</button>
          </div>
          <button onClick={exportCSV} className="flex items-center gap-2 bg-burgundy text-white px-4 py-2 rounded-full font-body text-sm">
            <FileText className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center font-body text-burgundy/70">Loading...</div>
          ) : filteredLeads.length === 0 ? (
            <div className="p-8 text-center font-body text-burgundy/70">No leads yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-burgundy/5">
                  <tr>
                    <th className="px-4 py-3 text-left font-h2 text-burgundy text-sm">Name</th>
                    <th className="px-4 py-3 text-left font-h2 text-burgundy text-sm">Email</th>
                    <th className="px-4 py-3 text-left font-h2 text-burgundy text-sm">Phone</th>
                    <th className="px-4 py-3 text-left font-h2 text-burgundy text-sm">Gender</th>
                    <th className="px-4 py-3 text-left font-h2 text-burgundy text-sm">Source</th>
                    <th className="px-4 py-3 text-left font-h2 text-burgundy text-sm">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead, i) => (
                    <tr key={lead.id} className={i % 2 === 0 ? 'bg-white' : 'bg-cream-light/50'}>
                      <td className="px-4 py-3 font-body text-burgundy">{lead.name}</td>
                      <td className="px-4 py-3 font-body text-burgundy">
                        <a href={`mailto:${lead.email}`} className="hover:underline">{lead.email}</a>
                      </td>
                      <td className="px-4 py-3 font-body text-burgundy">
                        {lead.phone && <a href={`tel:${lead.phone}`} className="hover:underline">{lead.phone}</a>}
                      </td>
                      <td className="px-4 py-3 font-body text-burgundy">
                        {lead.gender ? (
                          <span className={`px-2 py-1 rounded-full text-xs ${lead.gender === 'female' ? 'bg-pink-100 text-pink-800' : 'bg-blue-100 text-blue-800'}`}>
                            {lead.gender === 'female' ? '♀ Female' : '♂ Male'}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getSourceColor(lead.lead_source)}`}>
                          {getSourceLabel(lead.lead_source)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-body text-burgundy/70 text-sm">
                        {new Date(lead.created_at).toLocaleDateString()} {new Date(lead.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== TRANSFORM APP (PWA) ====================

const DAILY_MOTIVATIONS = [
  { id: 1, text: "God didn't bring you this far to leave you. Trust the process.", scripture: "Philippians 1:6" },
  { id: 2, text: "Your story isn't over. The best chapters are still being written.", scripture: "Jeremiah 29:11" },
  { id: 3, text: "You are not too much. You are not too little. You are exactly who God created you to be.", scripture: "Psalm 139:14" },
  { id: 4, text: "Stop waiting for permission to become who you were made to be.", scripture: "Esther 4:14" },
  { id: 5, text: "Rest is not a reward. It's a requirement. Take care of the vessel God gave you.", scripture: "Matthew 11:28" },
  { id: 6, text: "Comparison is a thief. Your journey is yours alone — and it's beautiful.", scripture: "Galatians 6:4" },
  { id: 7, text: "Fear will always whisper 'you can't.' Faith whispers 'watch me.'", scripture: "Isaiah 41:10" },
  { id: 8, text: "You don't have to have it all figured out. You just have to take the next step.", scripture: "Proverbs 3:5-6" },
  { id: 9, text: "Your past does not disqualify you. It prepares you.", scripture: "Romans 8:28" },
  { id: 10, text: "The woman you're becoming will cost you people, places, and things. She's worth it.", scripture: "Matthew 16:25" },
  { id: 11, text: "You were not created to blend in. You were created to stand out for His glory.", scripture: "Matthew 5:14" },
  { id: 12, text: "Healing isn't linear. Give yourself grace on the hard days.", scripture: "2 Corinthians 12:9" },
  { id: 13, text: "What God has for you, no one can take away. Walk in that confidence.", scripture: "Romans 8:31" },
  { id: 14, text: "Today is a gift. Use it to move one step closer to your purpose.", scripture: "Ephesians 5:15-16" }
];

const BOUNDARY_GUIDE = [
  { 
    title: "What Are Boundaries?", 
    content: "Boundaries are the limits you set to protect your peace, energy, and well-being. They're not walls — they're gates. You decide who and what gets access to you."
  },
  { 
    title: "Why Boundaries Matter", 
    content: "Without boundaries, you pour from an empty cup. You say yes when you mean no. You carry burdens that aren't yours. Boundaries aren't selfish — they're essential for the woman God is calling you to become."
  },
  { 
    title: "Signs You Need Stronger Boundaries", 
    content: "• You feel drained after certain conversations\n• You say yes out of guilt, not desire\n• You feel responsible for other people's emotions\n• You neglect your own needs to please others\n• You feel resentful but don't speak up"
  },
  { 
    title: "How to Set a Boundary", 
    content: "1. Identify what drains you\n2. Decide what you will and won't accept\n3. Communicate clearly and calmly\n4. Follow through — boundaries without action are just suggestions\n5. Release guilt. Boundaries honor you AND the relationship."
  },
  { 
    title: "Boundary Scripts to Use", 
    content: "• \"I love you, and I can't do that right now.\"\n• \"That doesn't work for me.\"\n• \"I need some time to think about that.\"\n• \"I'm not available for this conversation.\"\n• \"No.\" (It's a complete sentence.)"
  },
  { 
    title: "Remember", 
    content: "You teach people how to treat you. The boundaries you set today create the peace you'll live in tomorrow. You are worthy of protection — including from yourself."
  }
];

// Transform App - Signup Page
const TransformSignup = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', gender: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Listen for the PWA install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowInstallPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate gender selection
    if (!formData.gender) {
      setError('Please select Female or Male to continue.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/leads`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        instagram: null,
        lead_source: 'app-transform',
        gender: formData.gender
      });
      // Store user info for the app
      localStorage.setItem('transform_user', JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email
      }));
      navigate('/app/home');
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-light flex flex-col" data-testid="transform-signup">
      {/* Header with Logo */}
      <div className="bg-burgundy py-6 px-4 text-center">
        <img src="https://customer-assets.emergentagent.com/job_faith-transform-1/artifacts/0yw0xylc_Chelsea%20Flynn_Additional_red.png" alt="Chelsea Flynn Coaching" className="h-12 mx-auto mb-4" style={{filter: 'brightness(0) invert(1)'}} />
        <img src="https://customer-assets.emergentagent.com/job_faith-transform-1/artifacts/w8jkyebp_20250913-YAN02723.jpg" alt="Chelsea Flynn" className="w-20 h-20 rounded-full mx-auto object-cover border-4 border-white/30" />
      </div>

      {/* Install Instructions - Clean */}
      <div className="bg-burgundy/90 text-white px-4 py-4">
        <p className="font-h2 text-base text-center mb-2">📲 To install this app:</p>
        <p className="font-body text-base text-center text-cream-light mb-3">
          Tap <span className="inline-block bg-white/20 px-2 py-1 rounded font-bold">⬆️</span> at the bottom of Safari → <strong>"Add to Home Screen"</strong>
        </p>
        <button 
          onClick={() => {
            const message = encodeURIComponent("I'm using this free coaching app from Chelsea Flynn - daily motivation, journaling & more! Check it out: https://chelseaflynn.com/app");
            window.open(`https://wa.me/?text=${message}`, '_blank');
          }}
          className="w-full bg-green-500 text-white py-3 rounded-full font-h2 text-sm flex items-center justify-center gap-2"
        >
          <MessageCircle className="w-5 h-5" /> Share with Friends on WhatsApp
        </button>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <h2 className="font-h2 text-2xl text-burgundy mb-2">Welcome!</h2>
            <p className="font-body text-burgundy/70 text-base">Enter your details to access your free resources.</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                className="w-full px-4 py-4 rounded-xl border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy text-base"
                placeholder="First name"
                data-testid="transform-firstname"
              />
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                className="w-full px-4 py-4 rounded-xl border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy text-base"
                placeholder="Last name"
                data-testid="transform-lastname"
              />
            </div>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-4 rounded-xl border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy text-base"
              placeholder="Email address"
              data-testid="transform-email"
            />
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-4 py-4 rounded-xl border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy text-base"
              placeholder="Phone number"
              data-testid="transform-phone"
            />
            
            {/* Gender Selection */}
            <div className="pt-2">
              <p className="font-body text-burgundy/70 text-base mb-3 text-center">I am...</p>
              <div className="flex gap-4 justify-center">
                <label className={`flex items-center gap-2 px-6 py-3 rounded-full cursor-pointer transition-all border-2 ${formData.gender === 'female' ? 'bg-burgundy text-white border-burgundy' : 'bg-white text-burgundy border-burgundy/30 hover:border-burgundy'}`}>
                  <input
                    type="radio"
                    name="gender"
                    value="female"
                    checked={formData.gender === 'female'}
                    onChange={(e) => setFormData({...formData, gender: e.target.value})}
                    className="sr-only"
                    data-testid="transform-gender-female"
                  />
                  <span className="font-h2 text-base">Female</span>
                </label>
                <label className={`flex items-center gap-2 px-6 py-3 rounded-full cursor-pointer transition-all border-2 ${formData.gender === 'male' ? 'bg-burgundy text-white border-burgundy' : 'bg-white text-burgundy border-burgundy/30 hover:border-burgundy'}`}>
                  <input
                    type="radio"
                    name="gender"
                    value="male"
                    checked={formData.gender === 'male'}
                    onChange={(e) => setFormData({...formData, gender: e.target.value})}
                    className="sr-only"
                    data-testid="transform-gender-male"
                  />
                  <span className="font-h2 text-base">Male</span>
                </label>
              </div>
            </div>
            
            {error && <p className="text-red-600 font-body text-base text-center">{error}</p>}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-burgundy text-white py-4 rounded-full font-h1 text-lg hover:bg-burgundy/90 transition-colors disabled:opacity-50"
              data-testid="transform-submit"
            >
              {loading ? 'Please wait...' : 'Get Free Access'}
            </button>
          </form>
          
          <p className="font-body text-burgundy/50 text-sm text-center mt-4">
            Your information is safe and will never be shared.
          </p>
        </div>
      </div>

      {/* Social Links */}
      <div className="bg-burgundy/5 py-4 px-4 text-center">
        <p className="font-body text-burgundy/70 text-sm mb-3">Follow Chelsea</p>
        <div className="flex justify-center gap-6">
          <a href="https://instagram.com/chelseaflynncoaching" target="_blank" rel="noopener noreferrer" className="text-burgundy hover:text-burgundy/70">
            <Instagram className="w-6 h-6" />
          </a>
          <a href="https://youtube.com/@chelseaflynn1111" target="_blank" rel="noopener noreferrer" className="text-burgundy hover:text-burgundy/70">
            <Youtube className="w-6 h-6" />
          </a>
          <a href="https://www.facebook.com/profile.php?id=61576592120345" target="_blank" rel="noopener noreferrer" className="text-burgundy hover:text-burgundy/70">
            <Facebook className="w-6 h-6" />
          </a>
        </div>
      </div>
    </div>
  );
};

// Transform App - Install Banner (shows on every page)
const InstallBanner = () => {
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('install_banner_dismissed');
    if (wasDismissed) setDismissed(true);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('install_banner_dismissed', 'true');
  };

  const handleShare = () => {
    const message = encodeURIComponent("I'm using this free coaching app from Chelsea Flynn - daily motivation, journaling & more! Check it out: https://chelseaflynn.com/app");
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  if (isStandalone || dismissed) return null;

  return (
    <div className="bg-burgundy text-white px-4 py-4 relative">
      <button onClick={handleDismiss} className="absolute top-2 right-2 text-white/70 hover:text-white">
        <X className="w-5 h-5" />
      </button>
      <p className="font-h2 text-base text-center mb-2">📲 To install this app:</p>
      <p className="font-body text-base text-center text-cream-light mb-3">
        Tap <span className="inline-block bg-white/20 px-2 py-1 rounded font-bold">⬆️</span> at the bottom of Safari → <strong>"Add to Home Screen"</strong>
      </p>
      <button 
        onClick={handleShare}
        className="w-full bg-green-500 text-white py-3 rounded-full font-h2 text-sm flex items-center justify-center gap-2"
      >
        <MessageCircle className="w-5 h-5" /> Share with Friends on WhatsApp
      </button>
    </div>
  );
};

// Transform App - Bottom Navigation
const TransformNav = ({ active }) => {
  const navigate = useNavigate();
  const navItems = [
    { id: 'home', icon: '🌅', label: 'Today', path: '/app/home' },
    { id: 'journal', icon: '📓', label: 'Journal', path: '/app/journal' },
    { id: 'guide', icon: '📘', label: 'Boundaries', path: '/app/guide' },
    { id: 'coaching', icon: '✨', label: 'Coaching', path: '/app/coaching' },
    { id: 'connect', icon: '💬', label: 'Connect', path: '/app/connect' }
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-burgundy/10 px-2 py-2 z-50 safe-area-bottom">
      <div className="flex justify-around items-center max-w-md mx-auto">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => navigate(item.path)}
            className={`flex flex-col items-center py-2 px-3 rounded-xl transition-colors ${active === item.id ? 'bg-burgundy/10' : ''}`}
            data-testid={`nav-${item.id}`}
          >
            <span className="text-lg mb-1">{item.icon}</span>
            <span className={`font-body text-xs ${active === item.id ? 'text-burgundy font-semibold' : 'text-burgundy/60'}`}>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Transform App - Home (Daily Motivation)
const TransformHome = () => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('transform_user');
    if (!stored) {
      navigate('/app');
      return;
    }
    setUser(JSON.parse(stored));
  }, [navigate]);

  // Get today's motivation based on day of year
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  const todayMotivation = DAILY_MOTIVATIONS[dayOfYear % DAILY_MOTIVATIONS.length];

  if (!user) return null;

  return (
    <div className="min-h-screen bg-cream-light pb-28" data-testid="transform-home">
      {/* Install Banner */}
      <InstallBanner />
      
      {/* Header with Logo */}
      <div className="bg-burgundy py-5 px-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-body text-cream-light/80 text-base">Good {today.getHours() < 12 ? 'morning' : today.getHours() < 17 ? 'afternoon' : 'evening'},</p>
            <h1 className="font-h1 text-2xl text-white">{user.firstName}</h1>
          </div>
          <img src="https://customer-assets.emergentagent.com/job_faith-transform-1/artifacts/0yw0xylc_Chelsea%20Flynn_Additional_red.png" alt="" className="h-10" style={{filter: 'brightness(0) invert(1)'}} />
        </div>
      </div>

      {/* Free Version Banner */}
      <div className="bg-burgundy/10 px-4 py-3 text-center">
        <p className="font-body text-burgundy text-sm">🎁 <strong>Free Access</strong> — <button onClick={() => navigate('/app/coaching')} className="underline">Become a member</button> for full community features</p>
      </div>

      {/* Daily Motivation Card */}
      <div className="p-4">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-burgundy/10 px-4 py-3">
            <p className="font-h2 text-burgundy text-base">🌅 Today's Motivation</p>
            <p className="font-body text-burgundy/60 text-sm">{today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
          </div>
          <div className="p-6">
            <p className="font-h2 text-burgundy text-xl leading-relaxed mb-4">
              "{todayMotivation.text}"
            </p>
            <p className="font-body text-burgundy/60 text-base italic">— {todayMotivation.scripture}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/app/journal')}
            className="bg-white rounded-xl p-4 text-left shadow-sm hover:shadow-md transition-shadow"
          >
            <span className="text-2xl mb-2 block">📓</span>
            <p className="font-h2 text-burgundy text-base">Journal</p>
            <p className="font-body text-burgundy/60 text-sm">Reflect & write</p>
          </button>
          <button
            onClick={() => navigate('/app/guide')}
            className="bg-white rounded-xl p-4 text-left shadow-sm hover:shadow-md transition-shadow"
          >
            <span className="text-2xl mb-2 block">📘</span>
            <p className="font-h2 text-burgundy text-base">Boundary Guide</p>
            <p className="font-body text-burgundy/60 text-sm">Protect your peace</p>
          </button>
        </div>

        {/* CTA Banner */}
        <div className="mt-6 bg-burgundy rounded-2xl p-5 text-center">
          <p className="font-h2 text-white text-lg mb-2">Ready to go deeper?</p>
          <p className="font-body text-cream-light text-base mb-4">Book a free discovery call with Chelsea</p>
          <a
            href="https://calendly.com/chelseaflynncoaching/purpose-discovery-chat"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-burgundy px-6 py-3 rounded-full font-h1 text-base"
            data-testid="home-discovery-btn"
          >
            Book Free Call
          </a>
          <p className="font-body text-cream-light/70 text-sm mt-3">(Opens in new tab — close it to return)</p>
        </div>
      </div>

      <TransformNav active="home" />
    </div>
  );
};

// Transform App - Journal
const TransformJournal = () => {
  const [entries, setEntries] = useState([]);
  const [newEntry, setNewEntry] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('transform_user');
    if (!stored) {
      navigate('/app');
      return;
    }
    setUser(JSON.parse(stored));
    
    // Load saved entries
    const savedEntries = localStorage.getItem('transform_journal');
    if (savedEntries) setEntries(JSON.parse(savedEntries));
  }, [navigate]);

  const saveEntry = () => {
    if (!newEntry.trim()) return;
    
    const entry = {
      id: Date.now(),
      text: newEntry,
      date: new Date().toISOString()
    };
    
    const updated = [entry, ...entries];
    setEntries(updated);
    localStorage.setItem('transform_journal', JSON.stringify(updated));
    setNewEntry('');
  };

  const deleteEntry = (id) => {
    const updated = entries.filter(e => e.id !== id);
    setEntries(updated);
    localStorage.setItem('transform_journal', JSON.stringify(updated));
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-cream-light pb-28" data-testid="transform-journal">
      {/* Install Banner */}
      <InstallBanner />
      
      {/* Header */}
      <div className="bg-burgundy py-5 px-4">
        <h1 className="font-h1 text-2xl text-white">📓 My Journal</h1>
        <p className="font-body text-cream-light/80 text-base">A safe space for your thoughts</p>
      </div>

      <div className="p-4">
        {/* New Entry */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
          <textarea
            value={newEntry}
            onChange={(e) => setNewEntry(e.target.value)}
            placeholder="What's on your heart today?"
            className="w-full h-36 px-4 py-3 rounded-xl border border-burgundy/20 focus:border-burgundy outline-none font-body text-burgundy text-base resize-none"
            data-testid="journal-input"
          />
          <button
            onClick={saveEntry}
            disabled={!newEntry.trim()}
            className="mt-3 w-full bg-burgundy text-white py-4 rounded-full font-h1 text-base disabled:opacity-50"
            data-testid="journal-save"
          >
            Save Entry
          </button>
        </div>

        {/* Past Entries */}
        {entries.length > 0 && (
          <div>
            <h2 className="font-h2 text-burgundy text-base mb-3">Past Reflections</h2>
            <div className="space-y-3">
              {entries.map(entry => (
                <div key={entry.id} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-body text-burgundy/60 text-sm">
                      {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </p>
                    <button onClick={() => deleteEntry(entry.id)} className="text-burgundy/40 hover:text-burgundy">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="font-body text-burgundy text-base whitespace-pre-wrap">{entry.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {entries.length === 0 && (
          <div className="text-center py-8">
            <p className="font-body text-burgundy/50 text-base">Your journal is empty. Start writing!</p>
          </div>
        )}
      </div>

      <TransformNav active="journal" />
    </div>
  );
};

// Transform App - Boundary Guide
const TransformGuide = () => {
  const [activeSection, setActiveSection] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('transform_user');
    if (!stored) navigate('/app');
  }, [navigate]);

  return (
    <div className="min-h-screen bg-cream-light pb-28" data-testid="transform-guide">
      {/* Install Banner */}
      <InstallBanner />
      
      {/* Header */}
      <div className="bg-burgundy py-5 px-4">
        <h1 className="font-h1 text-2xl text-white">📘 Boundary Guide</h1>
        <p className="font-body text-cream-light/80 text-base">Protect your peace, honor your worth</p>
      </div>

      <div className="p-4">
        {/* Section Navigation */}
        <div className="flex overflow-x-auto gap-2 pb-3 mb-4 scrollbar-hide">
          {BOUNDARY_GUIDE.map((section, idx) => (
            <button
              key={idx}
              onClick={() => setActiveSection(idx)}
              className={`flex-shrink-0 px-4 py-2 rounded-full font-body text-sm transition-colors ${
                activeSection === idx ? 'bg-burgundy text-white' : 'bg-white text-burgundy'
              }`}
            >
              {section.title}
            </button>
          ))}
        </div>

        {/* Active Section Content */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="font-h2 text-burgundy text-xl mb-4">{BOUNDARY_GUIDE[activeSection].title}</h2>
          <p className="font-body text-burgundy/80 text-base leading-relaxed whitespace-pre-line">
            {BOUNDARY_GUIDE[activeSection].content}
          </p>
          
          {/* Navigation - BOLDER */}
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-burgundy/10">
            <button
              onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
              disabled={activeSection === 0}
              className="bg-burgundy/10 text-burgundy px-5 py-3 rounded-full font-h2 text-base disabled:opacity-30 disabled:bg-transparent"
            >
              ← Previous
            </button>
            <span className="font-body text-burgundy/60 text-sm">{activeSection + 1} / {BOUNDARY_GUIDE.length}</span>
            <button
              onClick={() => setActiveSection(Math.min(BOUNDARY_GUIDE.length - 1, activeSection + 1))}
              disabled={activeSection === BOUNDARY_GUIDE.length - 1}
              className="bg-burgundy text-white px-5 py-3 rounded-full font-h2 text-base disabled:opacity-30 disabled:bg-burgundy/30"
            >
              Next →
            </button>
          </div>
        </div>

        {/* Discovery Call CTA */}
        <div className="mt-6 bg-burgundy rounded-2xl p-5 text-center">
          <p className="font-h2 text-white text-lg mb-2">Need help setting boundaries?</p>
          <p className="font-body text-cream-light text-base mb-4">Let's talk about what's holding you back</p>
          <a
            href="https://calendly.com/chelseaflynncoaching/purpose-discovery-chat"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-burgundy px-6 py-3 rounded-full font-h1 text-base"
            data-testid="guide-discovery-btn"
          >
            Book Free Discovery Call
          </a>
          <p className="font-body text-cream-light/70 text-sm mt-3">(Opens in new tab — close it to return)</p>
        </div>
      </div>

      <TransformNav active="guide" />
    </div>
  );
};

// Transform App - Coaching (Work With Me)
const TransformCoaching = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('transform_user');
    if (!stored) navigate('/app');
  }, [navigate]);

  return (
    <div className="min-h-screen bg-cream-light pb-28" data-testid="transform-coaching">
      {/* Install Banner */}
      <InstallBanner />
      
      {/* Header */}
      <div className="bg-burgundy py-5 px-4">
        <h1 className="font-h1 text-2xl text-white">✨ Work With Chelsea</h1>
        <p className="font-body text-cream-light/80 text-base">Transform your life with faith-centered coaching</p>
      </div>

      {/* Free Version Notice */}
      <div className="bg-burgundy/10 px-4 py-3 text-center">
        <p className="font-body text-burgundy text-sm">🎁 You're using the <strong>Free Version</strong> — Upgrade for full community access</p>
      </div>

      <div className="p-4 space-y-4">
        {/* CFC Community - FEATURED */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-burgundy">
          <div className="bg-burgundy p-4">
            <p className="font-body text-cream-light text-sm mb-1">⭐ RECOMMENDED</p>
            <h2 className="font-h1 text-xl text-white">CFC Community</h2>
            <p className="font-body text-cream-light text-base">Monthly membership for ongoing transformation</p>
          </div>
          <div className="p-5">
            <ul className="space-y-2 mb-4">
              {['2 live group coaching sessions/month', 'Exclusive coaching videos', 'Private community access', 'Full app features unlocked', 'Worksheets & journal prompts'].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-burgundy mt-0.5 flex-shrink-0" />
                  <span className="font-body text-burgundy/80 text-base">{item}</span>
                </li>
              ))}
            </ul>
            <p className="font-h2 text-burgundy text-xl mb-4">US$200<span className="font-body text-base text-burgundy/60">/month</span></p>
            <a
              href="https://cfc-signup.caymangateway.online/"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-burgundy text-white py-4 rounded-full font-h1 text-base text-center"
              data-testid="coaching-cfc-btn"
            >
              Join the Community
            </a>
          </div>
        </div>

        {/* 1:1 Coaching */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-burgundy/90 p-4">
            <h2 className="font-h1 text-xl text-white">1:1 Life Transformation</h2>
            <p className="font-body text-cream-light text-base">8 weeks to unlock your purpose</p>
          </div>
          <div className="p-5">
            <ul className="space-y-2 mb-4">
              {['8 private coaching sessions', 'Personalized transformation plan', 'Weekly accountability', 'Direct access to Chelsea'].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-burgundy mt-0.5 flex-shrink-0" />
                  <span className="font-body text-burgundy/80 text-base">{item}</span>
                </li>
              ))}
            </ul>
            <p className="font-h2 text-burgundy text-xl mb-4">US$2,000</p>
            <a
              href="https://calendly.com/chelseaflynncoaching/purpose-discovery-chat"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-burgundy/90 text-white py-4 rounded-full font-h1 text-base text-center"
              data-testid="coaching-1on1-btn"
            >
              Book Discovery Call
            </a>
          </div>
        </div>

        {/* Breakthrough Session */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-burgundy/70 p-4">
            <h2 className="font-h1 text-xl text-white">Breakthrough Session</h2>
            <p className="font-body text-cream-light text-base">90 minutes of focused transformation</p>
          </div>
          <div className="p-5">
            <ul className="space-y-2 mb-4">
              {['Deep-dive coaching session', 'Clarity on one specific challenge', 'Actionable next steps', 'Recording to keep'].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-burgundy mt-0.5 flex-shrink-0" />
                  <span className="font-body text-burgundy/80 text-base">{item}</span>
                </li>
              ))}
            </ul>
            <p className="font-h2 text-burgundy text-xl mb-4">US$250</p>
            <a
              href="https://calendly.com/chelseaflynncoaching/purpose-discovery-chat"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-burgundy/70 text-white py-4 rounded-full font-h1 text-base text-center"
              data-testid="coaching-breakthrough-btn"
            >
              Book Discovery Call
            </a>
          </div>
        </div>

        {/* Free Call CTA */}
        <div className="bg-cream-dark rounded-2xl p-5 text-center">
          <p className="font-body text-burgundy text-base mb-3">Not sure which is right for you?</p>
          <a
            href="https://calendly.com/chelseaflynncoaching/purpose-discovery-chat"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-burgundy font-h2 text-base underline"
          >
            Let's chat — it's free →
          </a>
          <p className="font-body text-burgundy/50 text-sm mt-2">(Opens in new tab — close it to return)</p>
        </div>
      </div>

      <TransformNav active="coaching" />
    </div>
  );
};

// Transform App - Connect (Social & Email)
const TransformConnect = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('transform_user');
    if (!stored) {
      navigate('/app');
      return;
    }
    setUser(JSON.parse(stored));
  }, [navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-cream-light pb-28" data-testid="transform-connect">
      {/* Install Banner */}
      <InstallBanner />
      
      {/* Header */}
      <div className="bg-burgundy py-5 px-4">
        <h1 className="font-h1 text-2xl text-white">💬 Connect</h1>
        <p className="font-body text-cream-light/80 text-base">Stay connected with Chelsea</p>
      </div>

      <div className="p-4 space-y-4">
        {/* Email Chelsea */}
        <div className="bg-white rounded-2xl shadow-lg p-5">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-burgundy/10 rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-burgundy" />
            </div>
            <div>
              <h3 className="font-h2 text-burgundy text-lg">Email Chelsea</h3>
              <p className="font-body text-burgundy/60 text-sm">Get in touch directly</p>
            </div>
          </div>
          <a
            href="mailto:transform@chelseaflynncoaching.com"
            className="block w-full bg-burgundy text-white py-4 rounded-full font-h1 text-base text-center"
          >
            Send an Email
          </a>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-2xl shadow-lg p-5">
          <h3 className="font-h2 text-burgundy text-lg mb-4">Follow on Social Media</h3>
          
          <div className="space-y-3">
            <a
              href="https://instagram.com/chelseaflynncoaching"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 bg-cream-light rounded-xl hover:bg-burgundy/10 transition-colors"
            >
              <Instagram className="w-7 h-7 text-burgundy" />
              <div>
                <p className="font-h2 text-burgundy text-base">Instagram</p>
                <p className="font-body text-burgundy/60 text-sm">@chelseaflynncoaching</p>
              </div>
              <ChevronRight className="w-5 h-5 text-burgundy/40 ml-auto" />
            </a>
            
            <a
              href="https://youtube.com/@chelseaflynn1111"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 bg-cream-light rounded-xl hover:bg-burgundy/10 transition-colors"
            >
              <Youtube className="w-7 h-7 text-burgundy" />
              <div>
                <p className="font-h2 text-burgundy text-base">YouTube</p>
                <p className="font-body text-burgundy/60 text-sm">@chelseaflynn1111</p>
              </div>
              <ChevronRight className="w-5 h-5 text-burgundy/40 ml-auto" />
            </a>
            
            <a
              href="https://www.facebook.com/profile.php?id=61576592120345"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 bg-cream-light rounded-xl hover:bg-burgundy/10 transition-colors"
            >
              <Facebook className="w-7 h-7 text-burgundy" />
              <div>
                <p className="font-h2 text-burgundy text-base">Facebook</p>
                <p className="font-body text-burgundy/60 text-sm">Chelsea Flynn Coaching</p>
              </div>
              <ChevronRight className="w-5 h-5 text-burgundy/40 ml-auto" />
            </a>
          </div>
        </div>

        {/* Website */}
        <div className="bg-white rounded-2xl shadow-lg p-5">
          <h3 className="font-h2 text-burgundy text-lg mb-4">Visit Website</h3>
          <a
            href="https://chelseaflynn.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 bg-cream-light rounded-xl hover:bg-burgundy/10 transition-colors"
          >
            <Globe className="w-7 h-7 text-burgundy" />
            <div>
              <p className="font-h2 text-burgundy text-base">chelseaflynn.com</p>
              <p className="font-body text-burgundy/60 text-sm">Full website & resources</p>
            </div>
            <ChevronRight className="w-5 h-5 text-burgundy/40 ml-auto" />
          </a>
        </div>

        {/* Free Version Notice */}
        <div className="bg-burgundy/10 rounded-2xl p-5 text-center">
          <p className="font-body text-burgundy text-base mb-3">🎁 You're using the <strong>Free Version</strong></p>
          <button
            onClick={() => navigate('/app/coaching')}
            className="inline-block bg-burgundy text-white px-6 py-3 rounded-full font-h1 text-base"
          >
            Become a Member →
          </button>
        </div>
      </div>

      <TransformNav active="connect" />
    </div>
  );
};

// ==================== DISCOVERY CALL LANDING PAGE (AD TRAFFIC) ====================

const DiscoveryCallPage = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const successStories = [
    { 
      quote: "Chelsea is incredibly knowledgeable and passionate, and she really understands and supports you.", 
      name: "Suzanne R.", 
      image: "https://customer-assets.emergentagent.com/job_207e3b3a-41ee-4cf8-aebc-ff8609c9289a/artifacts/ki7x0e6f_Suzanne%20Image.jpg" 
    },
    { 
      quote: "I've noticed a shift in my internal dialogue and better resilience in combating negative thought patterns.", 
      name: "Fanisha N.", 
      image: "https://customer-assets.emergentagent.com/job_207e3b3a-41ee-4cf8-aebc-ff8609c9289a/artifacts/wxwh6581_Fanisha.jpg" 
    },
    { 
      quote: "It's incredibly refreshing to work with someone who deeply values her faith and makes time for daily devotionals.", 
      name: "Samantha P.", 
      image: "https://customer-assets.emergentagent.com/job_messiah-message/artifacts/oxb8r19g_429a397b-e139-44fd-ab0f-3ba8e1f65259.jpeg" 
    },
    { 
      quote: "This experience has helped me step out of my comfort zone and do things that add value to my purposeful life.", 
      name: "Meishka Z.", 
      image: "https://customer-assets.emergentagent.com/job_207e3b3a-41ee-4cf8-aebc-ff8609c9289a/artifacts/oo3f8di6_Meishka%20Image.jpg" 
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/leads`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        instagram: null,
        lead_source: 'ad-discovery-call'
      });
      window.location.href = 'https://calendly.com/chelseaflynncoaching/purpose-discovery-chat';
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-light" data-testid="discovery-call-page">
      {/* Header */}
      <div className="bg-burgundy py-4 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_faith-transform-1/artifacts/0yw0xylc_Chelsea%20Flynn_Additional_red.png" 
            alt="Chelsea Flynn Coaching" 
            className="h-8 mx-auto" 
            style={{filter: 'brightness(0) invert(1)'}} 
          />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
        
        {/* Hero Section */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_faith-transform-1/artifacts/w8jkyebp_20250913-YAN02723.jpg" 
            alt="Chelsea Flynn" 
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto object-cover border-4 border-burgundy/20 shadow-lg mb-4"
          />
          <h1 className="font-h1 text-2xl sm:text-3xl lg:text-4xl text-burgundy mb-3 leading-tight max-w-3xl mx-auto">
            Get tangible strategies to transform your life.
          </h1>
          <p className="font-body text-base sm:text-lg text-burgundy/80 max-w-2xl mx-auto">
            A practical, faith-aligned system to overcome anxiety and stress using neuroscience, CBT, and NLP — with the support you need to make it stick.
          </p>
        </div>

        {/* Two Column Layout - Form + Testimonials */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start mb-12">
          
          {/* Left Column - Form */}
          <div>
            <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-6">
              <h2 className="font-h2 text-lg sm:text-xl text-burgundy mb-1 text-center">Book Your Free Discovery Call</h2>
              <p className="font-body text-burgundy/60 text-sm text-center mb-4">Let's talk about what's possible for you ✨</p>
              
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy text-base"
                    placeholder="First name"
                    data-testid="discovery-firstname"
                  />
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy text-base"
                    placeholder="Last name"
                    data-testid="discovery-lastname"
                  />
                </div>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy text-base"
                  placeholder="Email address"
                  data-testid="discovery-email"
                />
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-burgundy/30 focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none font-body text-burgundy text-base"
                  placeholder="Phone number"
                  data-testid="discovery-phone"
                />
                
                {error && <p className="text-red-600 font-body text-sm text-center">{error}</p>}
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-burgundy text-white py-4 rounded-full font-h1 text-lg hover:bg-burgundy/90 transition-colors disabled:opacity-50"
                  data-testid="discovery-submit"
                >
                  {loading ? 'Please wait...' : 'Transform My Life →'}
                </button>
              </form>
              
              <p className="font-body text-burgundy/50 text-xs text-center mt-3">
                Your information is safe and will never be shared.
              </p>
            </div>
          </div>

          {/* Right Column - Testimonials */}
          <div className="space-y-4">
            <h3 className="font-h2 text-lg text-burgundy text-center lg:text-left mb-2">
              Real Women. Real Results.
            </h3>
            {successStories.map((story, i) => (
              <div key={i} className="bg-white rounded-xl p-4 shadow-md flex items-start gap-3">
                <img 
                  src={story.image} 
                  alt={story.name} 
                  className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                />
                <div>
                  <p className="font-body text-burgundy/80 italic text-sm leading-relaxed">"{story.quote}"</p>
                  <p className="font-h2 text-burgundy text-xs mt-1">— {story.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* This Is Not Just Life Coaching */}
        <div className="bg-burgundy rounded-2xl p-6 sm:p-8 mb-10 text-center">
          <h2 className="font-h1 text-xl sm:text-2xl text-white mb-3">This is not just "life coaching."</h2>
          <p className="font-body text-cream-light text-base sm:text-lg max-w-2xl mx-auto">
            You get to rewire how you think, feel, and live — with evidence-based neuroscience and spiritual guidance.
          </p>
        </div>

        {/* Simple 3-Step Process */}
        <div className="mb-12">
          <h2 className="font-h2 text-xl sm:text-2xl text-burgundy text-center mb-8">How It Works</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-burgundy text-white rounded-full flex items-center justify-center font-h1 text-xl mx-auto mb-4">1</div>
              <h3 className="font-h2 text-burgundy mb-2">Book Your Call</h3>
              <p className="font-body text-burgundy/70 text-sm">Schedule a free discovery call to discuss your goals and challenges.</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-burgundy text-white rounded-full flex items-center justify-center font-h1 text-xl mx-auto mb-4">2</div>
              <h3 className="font-h2 text-burgundy mb-2">Get Your Plan</h3>
              <p className="font-body text-burgundy/70 text-sm">We'll create a personalized roadmap based on your unique situation.</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-burgundy text-white rounded-full flex items-center justify-center font-h1 text-xl mx-auto mb-4">3</div>
              <h3 className="font-h2 text-burgundy mb-2">Transform</h3>
              <p className="font-body text-burgundy/70 text-sm">Start applying practical strategies with ongoing support to make lasting change.</p>
            </div>
          </div>
        </div>

        {/* This Is For / Not For */}
        <div className="grid sm:grid-cols-2 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-green-500">
            <h3 className="font-h2 text-burgundy mb-4 flex items-center gap-2">
              <span className="text-green-500">✓</span> This is for you if:
            </h3>
            <ul className="space-y-2 font-body text-burgundy/80 text-sm">
              <li>• You're tired of feeling stuck, anxious, or overwhelmed</li>
              <li>• You want faith-aligned guidance, not generic advice</li>
              <li>• You're ready to invest in yourself and do the work</li>
              <li>• You want practical tools, not just "positive thinking"</li>
            </ul>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-burgundy/30">
            <h3 className="font-h2 text-burgundy mb-4 flex items-center gap-2">
              <span className="text-burgundy/50">✗</span> This is not for you if:
            </h3>
            <ul className="space-y-2 font-body text-burgundy/60 text-sm">
              <li>• You're looking for overnight results</li>
              <li>• You're not open to faith-based perspectives</li>
              <li>• You want someone to fix your problems for you</li>
              <li>• You're not ready to commit to your growth</li>
            </ul>
          </div>
        </div>

        {/* Objection Handling FAQs */}
        <div className="bg-cream-light border border-burgundy/20 rounded-2xl p-6 sm:p-8 mb-12">
          <h2 className="font-h2 text-xl sm:text-2xl text-burgundy text-center mb-6">You Might Be Wondering...</h2>
          <div className="grid sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-4">
              <p className="font-h2 text-burgundy text-sm mb-2">"Is this just mindset talk?"</p>
              <p className="font-body text-burgundy/70 text-sm">No. This is practical tools — CBT, NLP, and neuroscience-backed strategies you can apply immediately.</p>
            </div>
            <div className="bg-white rounded-xl p-4">
              <p className="font-h2 text-burgundy text-sm mb-2">"Is this therapy or woo-woo stuff?"</p>
              <p className="font-body text-burgundy/70 text-sm">No. It's neuroscience-grounded and faith-aligned coaching. Evidence-based, spiritually rooted.</p>
            </div>
            <div className="bg-white rounded-xl p-4">
              <p className="font-h2 text-burgundy text-sm mb-2">"Will I actually be supported?"</p>
              <p className="font-body text-burgundy/70 text-sm">Yes. Choose 1:1 coaching or join our ongoing community — you're never doing this alone.</p>
            </div>
            <div className="bg-white rounded-xl p-4">
              <p className="font-h2 text-burgundy text-sm mb-2">"How long does this take?"</p>
              <p className="font-body text-burgundy/70 text-sm">Most clients see shifts within weeks. The 8-week programme creates lasting transformation.</p>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center bg-burgundy rounded-2xl p-8">
          <h3 className="font-h1 text-xl sm:text-2xl text-white mb-3">Ready to stop surviving and start thriving?</h3>
          <p className="font-body text-cream-light mb-6">The first step is always the hardest. Let's take it together.</p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-white text-burgundy px-8 py-4 rounded-full font-h1 text-lg hover:bg-cream-light transition-colors"
          >
            Book My Free Call
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-burgundy py-4 px-4 text-center mt-8">
        <p className="font-body text-cream-light text-sm">© {new Date().getFullYear()} Chelsea Flynn Coaching</p>
      </footer>
    </div>
  );
};

function App() {
  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/get-started" element={<GetStartedPage />} />
          <Route path="/start" element={<StartPage />} />
          <Route path="/services/executive-coaching" element={<LifeTransformationPage />} />
          <Route path="/services/life-transformation" element={<LifeTransformationPage />} />
          <Route path="/services/corporate-workshops" element={<CorporateWorkshopsPage />} />
          <Route path="/services/breakthrough-session" element={<BreakthroughSessionPage />} />
          <Route path="/services/speaking" element={<SpeakingEngagementsPage />} />
          <Route path="/free-guide" element={<FreeGuidePage />} />
          <Route path="/free-guide/purpose-worksheet" element={<PurposeWorksheetPage />} />
          <Route path="/stress-reset" element={<StressResetPage />} />
          <Route path="/stress-reset/toolkit" element={<StressResetToolkitPage />} />
          <Route path="/admin/leads" element={<LeadsDashboard />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/forum" element={<ForumPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          {/* Transform PWA App Routes */}
          <Route path="/app" element={<TransformSignup />} />
          <Route path="/app/home" element={<TransformHome />} />
          <Route path="/app/journal" element={<TransformJournal />} />
          <Route path="/app/guide" element={<TransformGuide />} />
          <Route path="/app/coaching" element={<TransformCoaching />} />
          <Route path="/app/connect" element={<TransformConnect />} />
          <Route path="/discovery" element={<DiscoveryCallPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
